import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as api from '../../services/api';
import { usePlatform } from '../../contexts/PlatformContext';
import useActiveScope from '../../hooks/useActiveScope';
import FrameworkPicker from './FrameworkPicker';
import {
  Upload, Cpu, CheckSquare, Link2, Zap, ArrowLeft, ArrowRight,
  X, FileText, AlertTriangle, RefreshCw, Check, ChevronDown, Trash2,
  Layers, Search, Shield, Lock, Database, Settings, Eye, GitBranch, FileCheck
} from 'lucide-react';

// Raised by the Stage-1 source gate. Held as a constant (rather than an inline
// literal at each raise site) so the auto-clear effect can identify precisely
// this error and clear it when the condition resolves — without string-matching
// a message that a later edit might reword.
const NO_SOURCE_ERROR = 'No sources selected — choose documents, patterns, or framework controls';
const INCOMPLETE_SOURCE_PREFIX = 'Finish selecting your enforcement sources — ';

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.08)',
};
const STONE = {
  50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4',
  400: '#a8a29e', 500: '#78716c', 600: '#57534e',
  700: '#44403c', 900: '#1c1917',
};

const STEPS = [
  { key: 'source', label: 'Source Selection', icon: Upload, desc: 'Select enforcement sources: policy documents, pattern templates, or both' },
  { key: 'processing', label: 'Extract & Generate', icon: Cpu, desc: 'Extract rules from documents and generate from pattern templates' },
  { key: 'review', label: 'Review & Select', icon: CheckSquare, desc: 'Review enforcement candidates from all sources and select which to apply' },
  { key: 'binding', label: 'Configure Binding', icon: Link2, desc: 'Choose agents, scopes, and enforcement points' },
  { key: 'activation', label: 'Activate', icon: Zap, desc: 'Test with dry-run and activate enforcement' },
];

// POLICY-PHASE2 — bridge deep-link stage keys → STEPS index. Accepts both the
// human stage names used in the agent panel CTA (embed/extract/review/bind/
// activate) and the wizard's own STEPS keys, so either spelling resolves.
const STAGE_KEY_TO_STEP = {
  embed: 0, source: 0,
  extract: 1, processing: 1,
  review: 2,
  bind: 3, binding: 3,
  activate: 4, activation: 4,
};

const PATTERN_DOMAIN_ICONS = {
  SECURITY: Shield, ACCESS_CONTROL: Lock, DATA_CLASSIFICATION: Database,
  CHANGE_MANAGEMENT: GitBranch, OPERATIONAL: Settings, COMPLIANCE: Eye,
};
const PATTERN_DOMAIN_COLORS = {
  SECURITY: { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
  ACCESS_CONTROL: { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  DATA_CLASSIFICATION: { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0' },
  CHANGE_MANAGEMENT: { bg: '#fefce8', text: '#854d0e', border: '#fef08a' },
  OPERATIONAL: { bg: '#faf5ff', text: '#6b21a8', border: '#e9d5ff' },
  COMPLIANCE: { bg: '#f0f9ff', text: '#075985', border: '#bae6fd' },
};

const SEVERITY_OPTIONS = ['BLOCK', 'WARN', 'AUDIT'];
const ENFORCEMENT_POINTS = ['ALL', 'WORKFLOW_ENTRY', 'INTER_STEP', 'CONNECTOR'];

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: `1px solid ${STONE[200]}`, fontSize: 13, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'Inter, sans-serif',
};

const selectStyle = {
  ...inputStyle, appearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath d=\'M6 8L1 3h10z\' fill=\'%2378716c\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: 28,
};

export default function PolicySkillWizard({
  config, onComplete, onCancel,
  preSelectedPatterns, preSourceMode, preBindingConfig,
  // Supplied ONLY by the FrameworkCoveragePanel "Open in Wizard" handoff. When the
  // operator instead picks a framework from this wizard's own inline picker, no binding
  // exists yet and the wizard must mint one itself — see the frameworkBindingId state below.
  frameworkBindingId: frameworkBindingIdProp,
  frameworkId, frameworkName,
  // Phase 4: handoff from FrameworkCoveragePanel gap rows. When set, the
  // wizard auto-selects only the framework candidates whose controlId
  // matches one of these UUIDs after Stage 2 extraction. Other framework
  // controls in the same framework still appear in the review table but
  // start unchecked, so the operator can broaden the selection if they
  // want. Pass a non-empty array to enable; null/empty = no filter.
  preSelectedFrameworkControlIds,
  // G441 — When the wizard is hosted inside a V2 Slideout (introduced in
  // PolicyConfigPageV2.jsx PolicySkillsInventory), the slideout chrome
  // already provides a title bar with a close button, so the wizard's
  // inline "Back" header + h1 title row is redundant. Callers set this
  // flag to suppress the duplicated chrome. Defaults to false so the
  // legacy full-page rendering path stays unchanged.
  embeddedInSlideout = false,
  // POLICY-PHASE2 — bridge deep-link from the agent's policy_skill panel
  // (/policy-config?skillId=…&stage=extract|bind). Maps a stage key to the
  // wizard's step index so the operator lands directly on Extract or Bind
  // instead of re-walking from Source Selection. Unknown / absent → step 0.
  initialStage = null,
  // When set (wizard opened from a specific agent's Configure deep-link), the
  // policy-skill picker is scoped to THIS agent's policy skills only — it must
  // never list other agents' skills. Null = tenant-wide (Policies-page entry).
  scopeAgentId = null,
}) {
  const [currentStep, setCurrentStep] = useState(() => STAGE_KEY_TO_STEP[initialStage] ?? 0);
  const [configId, setConfigId] = useState(config?.id || null);
  const [skillId, setSkillId] = useState(config?.skillId || '');
  const [documentIds, setDocumentIds] = useState([]);
  // Lifted from StepSource: the Extract stage needs document names to report a
  // per-document extraction failure, and the Source stage's Continue guard
  // needs to know whether any document is actually selectable.
  const [skillDocs, setSkillDocs] = useState([]);
  const [selectedPatterns, setSelectedPatterns] = useState(preSelectedPatterns || []);
  // Sources are multi-select. Any combination of {'framework','patterns','documents'}
  // is valid — the wizard extracts from all selected sources in one pass.
  // preSourceMode accepts a string (legacy: 'documents'|'patterns'|'both'|'framework')
  // OR an array (new). Initial value rules:
  //   • array preset → use it as-is
  //   • string 'both' → ['documents','patterns']
  //   • string 'documents'/'patterns'/'framework' → wrap into array
  //   • no preset, framework context present → default to ['framework']
  //   • no preset, no framework context → default to ['documents']
  const initialSourceModes = (() => {
    if (Array.isArray(preSourceMode)) return preSourceMode;
    if (preSourceMode === 'both') return ['documents', 'patterns'];
    if (preSourceMode === 'documents' || preSourceMode === 'patterns' || preSourceMode === 'framework') return [preSourceMode];
    if (frameworkId) return ['framework'];
    return ['documents'];
  })();
  const [sourceModes, setSourceModes] = useState(initialSourceModes);
  // ── Framework selection (wizard-local) ──────────────────────────────
  // The wizard accepts framework context as props (frameworkId / frameworkName)
  // when launched from the Framework Coverage Panel. When launched directly
  // from "New Policy Skill" with no preset, the operator picks a framework
  // from the dropdown that appears when the framework source tile is on.
  // Both paths feed into these effective fields, which are what every
  // downstream piece of logic reads.
  const [pickedFrameworkId, setPickedFrameworkId] = useState(frameworkId || null);
  const [pickedFrameworkName, setPickedFrameworkName] = useState(frameworkName || null);
  const effectiveFrameworkId = pickedFrameworkId || frameworkId;
  const effectiveFrameworkName = pickedFrameworkName || frameworkName;
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Binding granularity for the controls being accepted: 'agent' (default — the control
  // gates EVERY skill of the agent via the runtime PEP) or 'skill' (only this policy skill).
  const [bindScope, setBindScope] = useState('agent');
  // When opened from a specific agent's Configure (scopeAgentId), default the
  // binding to THAT agent — we're authoring this agent's policy, so pre-target it
  // rather than tenant-wide.
  const [bindingConfig, setBindingConfig] = useState(
    preBindingConfig
    || (scopeAgentId
      ? { scope: 'AGENT', enforcementPoints: ['ALL'], agentIds: [scopeAgentId] }
      : { scope: 'TENANT', enforcementPoints: ['ALL'], agentIds: [] }));
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Controls this policy skill already enforces — lifted to the wizard shell so
  // BOTH the Source and Configure-Binding steps can show "you're not starting
  // from scratch". One fetch, shared downward (no per-step re-fetch).
  const [materialized, setMaterialized] = useState([]);
  useEffect(() => {
    if (!skillId) { setMaterialized([]); return; }
    let cancelled = false;
    (async () => {
      const res = await api.getPolicySkillMaterialized(skillId);
      if (cancelled) return;
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setMaterialized(list);
    })();
    return () => { cancelled = true; };
  }, [skillId]);

  // Live binding stats reported up from StepBinding so the Activate summary
  // shows the SAME numbers the operator saw while binding — no hardcoding, no
  // divergence between the two pages.
  const [bindStats, setBindStats] = useState({ applicablePepCount: null, inheritedCount: null });

  // ── The SINGLE definition of "has the operator actually chosen a source?" ──
  // This predicate used to live only inside handleExtract (Stage 2). Stage 1
  // ("Source Selection") therefore had no guard at all: an operator could hit
  // Continue with 0 of 1 documents selected, watch Stage 1 mark itself ✓, and
  // only discover one stage later that they had selected nothing. The gate
  // belongs on the stage that owns the decision, so both stages read it here.
  const sourceSelection = useMemo(() => {
    const hasDocuments = sourceModes.includes('documents') && documentIds.length > 0;
    const hasPatterns = sourceModes.includes('patterns') && selectedPatterns.length > 0;
    const hasFramework = sourceModes.includes('framework') && !!effectiveFrameworkId;

    // An ENABLED source that carries no selection is not "ignorable" — it is an
    // unfinished instruction. Enabling Framework Controls and picking no framework
    // used to extract from documents alone and report success, so the operator was
    // told their framework had been compiled when it had never been read. Each
    // enabled mode must be satisfied on its own terms.
    const empty = [];
    if (sourceModes.includes('documents') && !hasDocuments) empty.push('Policy Documents — select at least one document');
    if (sourceModes.includes('patterns') && !hasPatterns) empty.push('Pattern Templates — select at least one pattern');
    if (sourceModes.includes('framework') && !hasFramework) empty.push('Framework Controls — choose a framework');

    return {
      hasDocuments, hasPatterns, hasFramework,
      any: hasDocuments || hasPatterns || hasFramework,
      emptyEnabledSources: empty,
      complete: (hasDocuments || hasPatterns || hasFramework) && empty.length === 0,
    };
  }, [sourceModes, documentIds, selectedPatterns, effectiveFrameworkId]);

  // A blocking error must not outlive the condition that raised it. The source
  // banner previously stayed on screen ("No sources selected") after the
  // operator had gone back and selected the document. Clear it — and ONLY it —
  // the moment the selection becomes valid; other errors are left untouched.
  useEffect(() => {
    if (sourceSelection.complete) {
      setError((e) => (e === NO_SOURCE_ERROR || String(e || '').startsWith(INCOMPLETE_SOURCE_PREFIX) ? null : e));
    }
  }, [sourceSelection.complete]);

  /** Raised when a source is switched on but left empty. Shared by both Stage-1 guards. */
  const incompleteSourceError = () =>
    `${INCOMPLETE_SOURCE_PREFIX}${sourceSelection.emptyEnabledSources.join('; ')}`;

  // Reads the PROP, not the state: the state const is declared below this line, and a const
  // referenced before its declaration is a TDZ ReferenceError at render, not a lint error.
  const [bindingStatus, setBindingStatus] = useState(frameworkBindingIdProp ? 'PENDING' : null); // Provenance tracking
  const [dryRunResults, setDryRunResults] = useState(null);
  const [autoCreatedSkill, setAutoCreatedSkill] = useState(null); // { id, name } of auto-created skill
  const [autoCreating, setAutoCreating] = useState(false);
  const autoCreateFailed = useRef(false); // Prevent retry loop on persistent failure

  const { agents } = usePlatform();
  // Active scope (JWT-truth: the pinned customer/org/tenant/instance). A Platform Admin
  // can SEE agents across every customer, but the policy-skill picker must list ONLY the
  // skills in the CURRENTLY ACTIVE tenant — otherwise it leaks every tenant's skills into
  // one dropdown. Used to scope the agents fetch + filter the derived skill list.
  const { tenantId: scopeTenantId, orgId: scopeOrgId } = useActiveScope();

  // ── The FrameworkBinding this wizard run is operating under ─────────────────────────
  //
  // RC (assigning a framework to a policy skill persisted NOTHING): this was a read-only
  // PROP, set only by the FrameworkCoveragePanel "Open in Wizard" handoff. Picking a
  // framework from the wizard's OWN inline picker set `effectiveFrameworkId` but left the
  // binding id null — and handleActivate gates the entire unified pipeline on
  // `if (frameworkBindingId)`. So the framework tile would show 8 automatable + 6
  // attestation controls, let the operator select all 14, announce "15 Rules to Enforce",
  // report a successful activation — and write no PolicyRule, no PolicyBinding, no
  // AttestationTask and no FrameworkBinding. The framework was decoration. That is why
  // every row in the Policies list reads FRAMEWORK "—".
  //
  // It is now STATE, seeded from the prop: the handoff path is unchanged, and the inline
  // picker path mints the same artifact (ensureFrameworkBinding) so both converge on the
  // one activate path, exactly as UNIFIED_POLICY_PIPELINE_SPEC requires.
  // Terminal outcome of stage 5 (Activate / Enable Testing Mode).
  //
  // Activation used to call onComplete() the instant the request resolved, so the slideout
  // simply vanished. The operator never saw WHAT was written — how many rules, how many
  // attestations, under which framework — and landed back on a list, not on the agent skill
  // they started from. A governance action that reports nothing is indistinguishable from one
  // that did nothing. The wizard now holds this terminal state and renders the result until
  // the operator dismisses it.
  const [outcome, setOutcome] = useState(null); // { ok, title, detail, counts[], data }

  const [frameworkBindingId, setFrameworkBindingId] = useState(frameworkBindingIdProp || null);
  useEffect(() => {
    if (frameworkBindingIdProp) setFrameworkBindingId(frameworkBindingIdProp);
  }, [frameworkBindingIdProp]);
  const [fetchedSkills, setFetchedSkills] = useState([]);
  const [allAgentSkills, setAllAgentSkills] = useState([]); // full list before filtering
  // The Configure-Binding agent picker needs the FULL agent list. PlatformContext
  // `agents` is frequently empty on the Policies / policy-config page (it's loaded
  // lazily on the Agents page), which made the picker show "0 of 0" even when we
  // came from a specific ExteLet. The skill picker dodged this with its own
  // getAgents fallback; give the binding picker the same. resolvedAgents prefers
  // the live context list and falls back to the fetched one.
  const [fetchedAgents, setFetchedAgents] = useState([]);
  const resolvedAgents = (agents && agents.length > 0) ? agents : fetchedAgents;
  useEffect(() => {
    if (agents && agents.length > 0) return;      // context has them — no fetch
    if (fetchedAgents.length > 0) return;          // already fetched
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAgents(scopeTenantId ? { tenantId: scopeTenantId } : {});
        if (cancelled) return;
        const raw = res?.ok ? (res.data || []) : (Array.isArray(res) ? res : []);
        setFetchedAgents(raw.filter(a => !scopeTenantId || a.tenantId === scopeTenantId));
      } catch (_) { /* falls back to empty; scope can still be Tenant/Org */ }
    })();
    return () => { cancelled = true; };
  }, [agents, fetchedAgents.length, scopeTenantId]);

  // Target agent IDs from preBindingConfig (when coming from Framework Coverage)
  const targetAgentIds = preBindingConfig?.agentIds || [];

  // Primary: derive skills from PlatformContext. When scopeAgentId is set, restrict
  // to THAT agent's policy skills only (agent-opened wizard must not list others').
  // Attach _agentName so the picker can disambiguate same-named skills.
  const contextSkills = (agents || [])
    .filter(a => !scopeAgentId || a.id === scopeAgentId)
    // Scope to the active tenant — never list other tenants' policy skills.
    .filter(a => !scopeTenantId || a.tenantId === scopeTenantId)
    // _agentId is stamped from the agent we just walked, rather than trusting the skill DTO
    // to carry agentId. A framework binds to an AGENT (framework_bindings.agent_id is NOT
    // NULL), so ensureFrameworkBinding needs this and must not depend on DTO shape.
    .flatMap(a => (a.skills || [])
      .filter(s => s.skillType === 'policy_skill')
      .map(s => ({ ...s, _agentId: a.id, _agentName: a.name }))) || [];

  // Fetch agent-skill mappings for filtering (always needed when agent-filtered)
  // Also serves as fallback for skills if PlatformContext doesn't have them
  useEffect(() => {
    // ALWAYS fetch the fresh, complete policy-skill list — do NOT trust contextSkills alone.
    // contextSkills is derived from PlatformContext.agents[].skills, which is NOT refreshed when a
    // policy skill is created on the Agent detail page. The old skip ("contextSkills present →
    // don't fetch") made that staleness permanent: a just-created skill was never in the picker,
    // so the Edit wizard opened on the placeholder and "going back" showed the skill missing too.
    let cancelled = false;
    (async () => {
      try {
        // Scope the fetch to the active tenant so a Platform Admin doesn't pull every
        // customer's agents into the picker (server-side scope; FE filter below is the
        // belt-and-suspenders in case the endpoint returns cross-scope for platform roles).
        const res = await api.getAgents(scopeTenantId ? { tenantId: scopeTenantId } : {});
        if (cancelled) return;
        const rawList = res.ok ? (res.data || []) : (Array.isArray(res) ? res : []);
        const agentList = rawList.filter(a => !scopeTenantId || a.tenantId === scopeTenantId);
        // Store agent-skill mapping for filtering
        const agentSkillMap = agentList.map(a => ({
          agentId: a.id,
          agentName: a.name,
          skills: (a.skills || []).filter(s => s.skillType === 'policy_skill'),
        }));
        setAllAgentSkills(agentSkillMap);
        // Always populate fetchedSkills from the fresh fetch (was gated on contextSkills.length===0,
        // which let stale context permanently hide newly-created skills). Merged with contextSkills below.
        const allSkills = agentList
            .filter(a => !scopeAgentId || a.id === scopeAgentId)
            .flatMap(a => (a.skills || [])
              .filter(s => s.skillType === 'policy_skill')
              .map(s => ({ ...s, _agentId: a.id, _agentName: a.name })));
        setFetchedSkills(allSkills);
      } catch (_) { /* ignore — falls to UUID input */ }
    })();
    return () => { cancelled = true; };
  }, [contextSkills.length, targetAgentIds.length, scopeTenantId]); // eslint-disable-line

  // Union of context + freshly-fetched skills, deduped by id. contextSkills gives an immediate
  // render; fetchedSkills is the fresh, complete list that includes just-created policy skills the
  // stale context is missing. Neither alone is sufficient — merge so the picker is always complete.
  const rawSkills = (() => {
    const byId = new Map();
    contextSkills.forEach(s => { if (s && s.id) byId.set(s.id, s); });
    fetchedSkills.forEach(s => { if (s && s.id && !byId.has(s.id)) byId.set(s.id, s); });
    return [...byId.values()];
  })();

  // Filter skills: if we have target agent IDs, only show skills belonging to those agents
  // CRITICAL: Do NOT fall back to all skills when agent has none — that's an integrity violation
  const skills = React.useMemo(() => {
    let base;
    if (targetAgentIds.length === 0) {
      base = rawSkills; // No agent filter — show all
    } else {
      const targetSet = new Set(targetAgentIds);
      // If we have allAgentSkills mapping (from fetched data), filter by agent
      if (allAgentSkills.length > 0) {
        base = allAgentSkills
          .filter(a => targetSet.has(a.agentId))
          .flatMap(a => a.skills);
      } else if (agents && agents.length > 0) {
        // If using contextSkills from PlatformContext, filter by matching agents
        base = agents
          .filter(a => targetSet.has(a.id))
          .flatMap(a => (a.skills || []).filter(s => s.skillType === 'policy_skill'));
      } else {
        base = []; // Agent-filtered but no data source — show empty, not all
      }
    }
    // Editing an existing config: GUARANTEE its own skill is present + pre-selectable, even if the
    // (stale) context and the in-flight fetch haven't surfaced it yet. Without this the wizard opens
    // on the placeholder and the operator can't re-select the very skill they clicked Edit on.
    if (config?.skillId && !base.some(s => s && s.id === config.skillId)) {
      base = [{
        id: config.skillId,
        name: config.skillName || 'Policy skill',
        description: config.description || '',
        skillType: 'policy_skill',
        _agentId: config.agentId || null,
      }, ...base];
    }
    return base;
  }, [rawSkills, targetAgentIds, allAgentSkills, agents, config]);

  // Auto-create policy skill when agent has none and we're in framework binding mode
  useEffect(() => {
    if (autoCreatedSkill) return; // Already created
    if (autoCreating) return; // In progress
    if (autoCreateFailed.current) return; // Failed previously — don't retry
    if (!frameworkBindingId) return; // Not in binding mode
    if (targetAgentIds.length === 0) return; // No target agent
    if (skills.length > 0) return; // Agent already has policy skills
    if (config) return; // Editing existing config

    // Wait for allAgentSkills to be loaded before deciding (avoid premature auto-create)
    if (allAgentSkills.length === 0 && contextSkills.length === 0) return;

    const targetAgentId = targetAgentIds[0];
    const agentName = agents?.find(a => a.id === targetAgentId)?.name || 'Agent';
    const autoSkillName = `${frameworkName || 'Framework'} Policy - ${agentName}`;

    setAutoCreating(true);
    (async () => {
      try {
        // Phase 7 — idempotent get-or-create. The backend reuses an
        // existing auto-skill for this (agent, framework binding) pair
        // instead of letting duplicates accumulate on re-bind.
        const skillRes = await api.autoCreatePolicySkill(targetAgentId, {
          frameworkBindingId,
          name: autoSkillName,
          description: `Auto-created policy skill for ${frameworkName || 'framework'} enforcement binding on ${agentName}`,
        });
        if (skillRes.ok && skillRes.data?.id) {
          const newSkill = { id: skillRes.data.id, name: skillRes.data.name || autoSkillName };
          setAutoCreatedSkill(newSkill);
          setSkillId(skillRes.data.id);
          // Update local skills list so dropdown shows it
          setFetchedSkills(prev => [...prev, { ...skillRes.data, name: autoSkillName, skillType: 'policy_skill' }]);
          // Also add to allAgentSkills so the filter picks it up
          setAllAgentSkills(prev => prev.map(a =>
            a.agentId === targetAgentId
              ? { ...a, skills: [...a.skills, { ...skillRes.data, name: autoSkillName, skillType: 'policy_skill' }] }
              : a
          ));
          console.info(`[Provenance] Auto-created policy skill "${autoSkillName}" (${skillRes.data.id}) for agent ${targetAgentId}`);
        } else {
          // Non-ok response (e.g. 500) — stop retrying
          autoCreateFailed.current = true;
          setError(`Failed to auto-create policy skill: ${skillRes.error || 'Server error'}`);
        }
      } catch (e) {
        autoCreateFailed.current = true; // Prevent retry loop
        console.error('[Provenance] Failed to auto-create policy skill:', e);
        setError(`Failed to auto-create policy skill: ${e.message}`);
      } finally {
        setAutoCreating(false);
      }
    })();
  }, [frameworkBindingId, targetAgentIds, skills.length, allAgentSkills.length, contextSkills.length, autoCreatedSkill, autoCreating, config]); // eslint-disable-line

  // Load existing config data
  useEffect(() => {
    if (config) {
      setSkillId(config.skillId);
      try {
        const docIds = config.documentIds ? (typeof config.documentIds === 'string' ? JSON.parse(config.documentIds) : (Array.isArray(config.documentIds) ? config.documentIds : [])) : [];
        setDocumentIds(docIds);
      } catch { setDocumentIds([]); }

      setConfigId(config.id);
      const status = config.activationStatus;
      if (status === 'testing') {
        loadCandidates(config.skillId);
        setCurrentStep(4); // Activation step — user can promote to production
        setDryRunResults({ status: 'testing', message: 'Policy skill is in testing mode. Rules are evaluated but not enforced. Click "Activate Policy Skill" to enable full production enforcement.' });
      } else if (status && status !== 'draft') {
        loadCandidates(config.skillId);
        // Honor an EXPLICIT deep-link stage even for an already-configured skill. The
        // "Extract & bind in Policies" CTA opens with ?stage=extract precisely so the
        // operator can add MORE sources (e.g. framework controls) to an existing policy
        // skill. Hard-jumping to Review (step 2) regardless stranded them past the
        // source/extract step, forcing a manual "go back" to add framework controls.
        // Default to Review only when no explicit stage was requested.
        const explicitStage = initialStage != null && STAGE_KEY_TO_STEP[initialStage] != null;
        setCurrentStep(explicitStage ? STAGE_KEY_TO_STEP[initialStage] : 2);
      } else {
        // DRAFT — resume from server-persisted progress so the operator picks up
        // where they left off instead of restarting at Source Selection. If
        // candidates were already extracted, land on Review (rehydrating the
        // selection); otherwise stay on the current (Source) step. Never downgrade
        // a step a deep-link explicitly requested (e.g. ?stage=bind).
        (async () => {
          try {
            const res = await api.getEnforcementCandidates(config.skillId);
            const list = res?.ok ? (res.data || []) : [];
            if (list.length > 0) {
              setCandidates(list);
              const accepted = list.filter(c => c.status === 'accepted' || c.status === 'customized');
              setSelectedIds(new Set(accepted.map(c => c.id)));
              // Honor an EXPLICIT deep-link stage. "Extract & bind in Policies" opens with
              // ?stage=extract precisely so the operator can re-extract / add framework
              // controls as an additional source — auto-jumping to Review (because prior
              // candidates exist) stranded them past the source/extract step. Only
              // auto-advance to Review when NO explicit stage was requested.
              const explicitStage = initialStage != null && STAGE_KEY_TO_STEP[initialStage] != null;
              if (!explicitStage) {
                setCurrentStep(prev => (prev < 2 ? 2 : prev));
              }
            }
          } catch (e) { /* no prior progress — start fresh at Source */ }
        })();
      }
    }
  }, [config, initialStage]); // eslint-disable-line

  const loadCandidates = useCallback(async (sid) => {
    try {
      const res = await api.getEnforcementCandidates(sid);
      if (res.ok) {
        setCandidates(res.data || []);
        const accepted = (res.data || []).filter(c => c.status === 'accepted' || c.status === 'customized');
        setSelectedIds(new Set(accepted.map(c => c.id)));
      }
    } catch (e) { console.error('Failed to load candidates', e); }
  }, []);

  /**
   * Get — or mint — the FrameworkBinding for the framework the operator picked inline.
   *
   * The binding is the provenance spine of the whole framework path: PolicyRule,
   * PolicyBinding and AttestationTask all carry `framework_binding_id` back to it, the
   * five-stage state machine hangs off it, and handleActivate routes to the unified
   * /policy/activate commit only when one exists. No binding => the framework contributes
   * nothing but pixels.
   *
   * ccCreateFrameworkBinding is idempotent server-side (V181 makes the uniqueness partial on
   * status <> ARCHIVED, and createBinding resurrects an ARCHIVED/FAILED row to PENDING), so
   * re-entering the wizard for the same (framework, agent) reuses the binding rather than
   * stacking a second one.
   *
   * @returns the binding id, or null after surfacing an error (caller must abort)
   */
  const ensureFrameworkBinding = async (effectiveSkillId) => {
    // framework_bindings.agent_id is NOT NULL — a framework binds to an AGENT, and the skill
    // is what the binding produces. Resolve the owning agent from the skill; fall back to the
    // agent the wizard was scoped to.
    const skill = (skills || []).find(s => String(s.id) === String(effectiveSkillId));
    const agentId = skill?._agentId || skill?.agentId || scopeAgentId || targetAgentIds[0] || null;
    if (!agentId) {
      setError(
        'This policy skill is not attached to an agent, so a compliance framework cannot be '
        + 'bound to it. Attach the skill to an agent first, or use documents and pattern '
        + 'templates as sources.'
      );
      return null;
    }
    const agentName = resolvedAgents.find(a => String(a.id) === String(agentId))?.name || null;

    // STEP 1 — the ComplianceConfig (the "this scope must comply with framework X" record).
    //
    // This is NOT optional bookkeeping. FrameworkBindingService.createBinding resolves a
    // complianceConfigId and THROWS when it cannot find one ("Ensure a compliance requirement
    // is configured before creating a binding"), which surfaces as a bare 400. So a binding
    // cannot exist before the requirement does. The platform's other framework callers
    // (GelPolicyWizard, the Control Config tab) already do this two-step; the policy-skill
    // wizard is simply reusing the same contract rather than inventing a third one.
    const configParams = { frameworkId: effectiveFrameworkId, complianceLevel: 'REQUIRED' };
    if (scopeOrgId) configParams.orgId = scopeOrgId;
    if (scopeTenantId) configParams.tenantId = scopeTenantId;
    if (agentId) configParams.agentId = agentId;
    const cfgRes = await api.ccAddFrameworkRequirement(configParams);
    if (!cfgRes?.ok) {
      setError(
        `Could not apply ${effectiveFrameworkName || 'the framework'} to this agent: `
        + `${cfgRes?.error || 'the compliance requirement could not be created'}`
      );
      return null;
    }

    // STEP 2 — the FrameworkBinding (the provenance spine every artifact hangs off).
    // Idempotent server-side: an existing live binding for (framework, agent) is returned
    // rather than duplicated, and an ARCHIVED/FAILED one is resurrected to PENDING.
    const res = await api.ccCreateFrameworkBinding({
      complianceConfigId: cfgRes.data?.id ?? null,
      frameworkId: effectiveFrameworkId,
      agentId,
      agentName,
      orgId: scopeOrgId || null,
      tenantId: scopeTenantId || null,
    });
    const binding = res?.data ?? res;
    if (!res?.ok || !binding?.id) {
      setError(`Could not bind ${effectiveFrameworkName || 'the framework'}: ${res?.error || 'binding was not created'}`);
      return null;
    }
    setFrameworkBindingId(binding.id);
    setBindingStatus(binding.status || 'PENDING');
    return binding.id;
  };

  // ── Step 1: Upload/Select Documents ─────────────────────
  const handleCreateConfig = async () => {
    // Use auto-created skill if available, otherwise use selected skillId
    let effectiveSkillId = skillId || autoCreatedSkill?.id;
    let wasAutoCreated = !!autoCreatedSkill && effectiveSkillId === autoCreatedSkill.id;

    if (autoCreating) { setError('Policy skill is being auto-created, please wait...'); return; }
    if (!effectiveSkillId) { setError('Please select a policy skill'); return; }
    // Stage-1 gate: a policy config with no source is not a config, and a source
    // switched on but left empty is an unfinished instruction — not an ignorable one.
    // Guarded here as well as on the button so a programmatic/resumed entry can't slip past.
    if (!sourceSelection.any) { setError(NO_SOURCE_ERROR); return; }
    if (!sourceSelection.complete) { setError(incompleteSourceError()); return; }
    setError(null);
    setProcessing(true);
    try {
      // A framework chosen from the inline picker has no FrameworkBinding behind it yet.
      // Mint one BEFORE the config is created, so the rest of the wizard — provenance
      // advancement, stage-5 activation, coverage — runs on the same rails as the
      // Coverage-Panel handoff. Without this the framework is display-only: see the
      // frameworkBindingId state declaration for the full failure.
      let bindingId = frameworkBindingId;
      if (sourceSelection.hasFramework && !bindingId) {
        bindingId = await ensureFrameworkBinding(effectiveSkillId);
        if (!bindingId) return;   // ensureFrameworkBinding already surfaced the reason
      }

      const res = await api.createPolicyConfig({ skillId: effectiveSkillId, documentIds });
      if (res.ok) {
        setConfigId(res.data.id);

        // Advance FrameworkBinding state: PENDING → SKILL_CREATED
        if (bindingId) {
          try {
            // bindingId, not the state — setFrameworkBindingId has not flushed yet in this tick.
            await api.ccAdvanceFrameworkBinding(bindingId, {
              targetStatus: 'SKILL_CREATED',
              policySkillId: effectiveSkillId,
              policySkillName: wasAutoCreated
                ? (autoCreatedSkill?.name || `${frameworkName || 'Framework'} Policy`)
                : (skills.find(s => s.id === effectiveSkillId)?.name || effectiveSkillId),
              skillAutoCreated: wasAutoCreated,
            });
            setBindingStatus('SKILL_CREATED');
          } catch (e) { console.warn('[Provenance] Failed to advance binding to SKILL_CREATED:', e); }
        }

        setCurrentStep(1);
      } else {
        if (bindingId) {
          try { await api.ccAdvanceFrameworkBinding(bindingId, { targetStatus: 'FAILED', failureReason: res.error || 'Policy config creation failed' }); } catch (_) {}
        }
        setError(res.error || 'Failed to create configuration');
      }
    } catch (e) {
      if (frameworkBindingId) {
        try { await api.ccAdvanceFrameworkBinding(frameworkBindingId, { targetStatus: 'FAILED', failureReason: e.message }); } catch (_) {}
      }
      setError(e.message);
    }
    finally { setProcessing(false); }
  };

  // ── Step 2: Extract from documents + Generate from patterns + Framework ──
  // Spec: docs/UNIFIED_POLICY_PIPELINE_SPEC.md "Stage 2: EXTRACT"
  const handleExtract = async () => {
    // Framework branch pulls automatable controls as rule candidates and manual
    // controls as attestation candidates; combinable with documents and patterns
    // in the same extraction pass. Predicate is shared with the Stage-1 guard.
    const { hasDocuments, hasPatterns, hasFramework } = sourceSelection;
    if (!sourceSelection.any) { setError(NO_SOURCE_ERROR); return; }
    if (!sourceSelection.complete) { setError(incompleteSourceError()); return; }
    setError(null);
    setProcessing(true);
    try {
      let allCandidates = [];
      // Extraction is FAIL-CLOSED. Every source the operator selected must
      // succeed, or the stage aborts. A partial result rendered as "here are
      // your candidates" is worse than an error: the operator concludes their
      // policy document contains no enforceable rules, when in fact extraction
      // crashed. Failures accumulate here and are surfaced together.
      const sourceFailures = [];

      // Extract from framework controls — calls the unified extract endpoint.
      // Yields BOTH rule candidates (automatable controls) and attestation
      // candidates (manual controls). Attestation candidates are stamped
      // _source='framework-attestation' so the review step can route them
      // separately from rule candidates if needed.
      if (hasFramework) {
        const res = await api.extractPolicyCandidates({
          frameworkBindingId: frameworkBindingId || null,
          sources: [{ type: 'FRAMEWORK', ref: effectiveFrameworkId }],
        });
        if (res.ok) {
          const ruleCands = (res.data?.ruleCandidates || []).map(c => ({
            id: c.id,
            proposedRuleName: c.name,
            proposedRuleType: c.ruleType || 'CUSTOM_SCRIPT',
            proposedSeverity: c.severity || 'WARN',
            confidenceScore: Math.round((c.confidence || 1) * 100),
            sourcePassage: c.description || '',
            sectionHeader: 'Framework Control',
            languageClarity: 100, patternMatchQuality: 100,
            paramExtractability: 100, contextConsistency: 100,
            _source: 'framework',
            _frameworkControlMappings: c.frameworkControlMappings || [],
            // The control's own rule template. This was hardcoded to {} — every framework
            // control therefore became a PolicyRule with an empty config: unenforceable if
            // typed (an ENUM with no values makes the engine block everything), and inert if
            // CUSTOM_SCRIPT (a script with no script). The backend now emits the control's
            // defaultRuleConfig and only classifies a control as automatable when that config
            // is actually enforceable; honour it here rather than throwing it away.
            ruleConfig: c.ruleConfig || {},
          }));
          allCandidates = [...allCandidates, ...ruleCands];
          // Stash attestation candidates so the activate step can include
          // them in the request payload. We display these inline in the
          // review step beneath the rule candidates.
          const attestCands = (res.data?.attestationCandidates || []).map(a => ({
            id: a.id,
            proposedRuleName: a.controlName,
            proposedRuleType: 'ATTESTATION',
            proposedSeverity: 'AUDIT',
            confidenceScore: 100,
            sourcePassage: a.controlDescription || '',
            sectionHeader: a.controlCode || 'Manual Control',
            _source: 'framework-attestation',
            _attestationCandidate: a,
            languageClarity: 100, patternMatchQuality: 100,
            paramExtractability: 0, contextConsistency: 100,
          }));
          allCandidates = [...allCandidates, ...attestCands];
        } else {
          sourceFailures.push(`Framework controls: ${res.error || 'extraction failed'}`);
        }
      }

      // Extract from documents (existing RAG flow)
      if (hasDocuments) {
        for (const docId of documentIds) {
          const res = await api.triggerExtraction(docId, skillId);
          if (!res.ok) {
            const doc = skillDocs.find(d => String(d.id) === String(docId));
            sourceFailures.push(`${doc?.filename || doc?.name || 'Document'}: ${res.error || 'extraction failed'}`);
          }
          // NOTE: res.data.candidates is deliberately NOT used to build the review list.
          // See the authoritative read below.
        }

        // The review list is the skill's PENDING candidate set — NOT the delta this run
        // happened to mint.
        //
        // RC: extraction is idempotent, so it persists and returns only candidates that
        // are NEW for this skill (a re-run must not duplicate rows). The wizard used that
        // response AS the review list, so re-running Extract — exactly what an operator
        // does after adding a framework to an existing skill — silently dropped every
        // previously-extracted candidate they had not yet accepted. Those rows stayed
        // `pending` in the database, still unmaterialized, and became unreachable from the
        // only surface that can review them. The stepper then said "14 of 14 selected"
        // while three controls awaiting a decision were nowhere on screen.
        //
        // Read the authoritative set instead. Accepted/rejected ones are correctly absent:
        // they have already been decided.
        const pending = await api.getEnforcementCandidates(skillId, 'pending');
        if (pending.ok) {
          const docCandidates = (pending.data || []).map(c => ({ ...c, _source: 'document' }));
          allCandidates = [...allCandidates, ...docCandidates];
        } else {
          sourceFailures.push(`Pending controls could not be loaded: ${pending.error || 'read failed'}`);
        }
      }

      // Generate candidates from selected patterns (template instantiation)
      if (hasPatterns) {
        for (const pattern of selectedPatterns) {
          const patternCandidate = {
            id: `pat-${pattern.id}-${Date.now()}`,
            proposedRuleName: pattern.patternName,
            proposedRuleType: pattern.mapsToRuleType || 'CUSTOM_SCRIPT',
            proposedSeverity: 'BLOCK',
            confidenceScore: Math.round((pattern.confidenceWeight || 0.85) * 100),
            sourcePassage: pattern.exampleText || `Generated from pattern: ${pattern.patternName}`,
            sectionHeader: pattern.domain,
            languageClarity: 95,
            patternMatchQuality: 100,
            paramExtractability: Math.round((pattern.confidenceWeight || 0.85) * 100),
            contextConsistency: 90,
            patternId: pattern.id,
            patternName: pattern.patternName,
            _source: 'pattern',
            _patternDomain: pattern.domain,
            ruleConfig: pattern.extractionTemplate || {},
          };
          allCandidates.push(patternCandidate);
        }
      }

      // Deduplicate: if same ruleType + domain from both sources, merge (keep higher confidence)
      if (hasDocuments && hasPatterns) {
        const seen = new Map();
        const deduped = [];
        for (const c of allCandidates) {
          const key = `${c.proposedRuleType}::${c.sectionHeader || c._patternDomain || ''}`;
          const existing = seen.get(key);
          if (existing) {
            // Mark as dual-source, keep higher confidence
            if (c.confidenceScore > existing.confidenceScore) {
              existing.confidenceScore = c.confidenceScore;
            }
            existing._source = 'both';
            existing._mergedCount = (existing._mergedCount || 1) + 1;
          } else {
            seen.set(key, c);
            deduped.push(c);
          }
        }
        allCandidates = deduped;
      }

      // FAIL-CLOSED. If any selected source failed to extract, abort the stage:
      // do NOT set candidates, do NOT advance the stepper. Advancing here would
      // render "0 of 0 selected / No candidates match the current filter" — an
      // operator reads that as "my policy has no enforceable rules" when the
      // truth is that extraction crashed server-side.
      if (sourceFailures.length > 0) {
        setError(
          sourceFailures.length === 1
            ? `Extraction failed — ${sourceFailures[0]}`
            : `Extraction failed for ${sourceFailures.length} sources — ${sourceFailures.join('; ')}`
        );
        return;
      }

      setCandidates(allCandidates);
      // ── Auto-select strategy ─────────────────────────────────────
      //
      // Two cases:
      //
      // (a) preSelectedFrameworkControlIds was supplied (Phase 4 handoff
      //     from Coverage Panel "Open in Wizard"). Auto-select ONLY
      //     framework-sourced candidates whose extracted id encodes one
      //     of those control UUIDs. Other candidates remain unchecked
      //     so the admin can opt in.
      //
      // (b) Default — auto-select every candidate at >=75% confidence
      //     (the historical heuristic; preserved exactly for legacy
      //     pattern + document flows).
      let autoIds;
      if (Array.isArray(preSelectedFrameworkControlIds) && preSelectedFrameworkControlIds.length > 0) {
        const wanted = new Set(preSelectedFrameworkControlIds.map(String));
        autoIds = allCandidates
          .filter(c => {
            // Framework rule candidates: id = "framework-<controlUuid>"
            // Framework attestation candidates: id = "attest-<controlUuid>"
            if (c._source !== 'framework' && c._source !== 'framework-attestation') return false;
            const idStr = String(c.id || '');
            const uuid = idStr.replace(/^framework-|^attest-/, '');
            return wanted.has(uuid);
          })
          .map(c => c.id);
      } else {
        autoIds = allCandidates.filter(c => c.confidenceScore >= 75).map(c => c.id);
      }
      setSelectedIds(new Set(autoIds));
      setCurrentStep(2);
    } catch (e) { setError(e.message); }
    finally { setProcessing(false); }
  };

  // ── Step 3: Accept selected candidates ──────────────────
  // Candidates arrive from two sources:
  //   • document extraction → real EnforcementCandidate rows with UUID ids
  //   • pattern selection   → synthetic client-side objects keyed as `pat-{patternId}-{timestamp}`
  // Document candidates route through batchUpdate (status → accepted).
  // Pattern candidates skip the candidate table entirely and mint PolicyRule + PolicyBinding
  // directly via /accept-patterns, because document_id is NOT NULL in the candidate schema
  // and pattern-sourced ones have no source chunk.
  const handleAcceptSelected = async () => {
    setError(null);
    setProcessing(true);
    try {
      const ids = Array.from(selectedIds);
      if (ids.length > 0) {
        const byId = new Map(candidates.map(c => [c.id, c]));
        const documentCandidateIds = [];
        const patternUuidsToAccept = [];
        for (const id of ids) {
          const c = byId.get(id);
          if (c && c._source === 'pattern' && c.patternId) {
            // Pattern-sourced: use the real pattern UUID, not the synthetic pat-* id
            patternUuidsToAccept.push(c.patternId);
          } else if (typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
            // Real candidate UUID from document extraction
            documentCandidateIds.push(id);
          } else if (c && c._source === 'both' && c.patternId) {
            // Dual-sourced: persist as pattern-backed rule so binding is captured server-side
            patternUuidsToAccept.push(c.patternId);
          }
          // else: silently drop — defensive skip of malformed entries so we never POST a
          // non-UUID into a List<UUID> endpoint (which previously produced the 500).
        }

        if (documentCandidateIds.length > 0) {
          // Document candidates MUST be materialized into a PolicyRule + PolicyBinding
          // via /candidates/{id}/accept — NOT merely flipped to 'accepted'. The old
          // batchUpdateCandidates('accepted') path wrote no binding, so the per-skill
          // "Bound runtime controls" panel (findBySkillId) stayed 0 even after binding.
          // acceptCandidate is idempotent + self-healing, so re-accepting an orphaned
          // (accepted-without-rule) candidate repairs it.
          //
          // FAIL-CLOSED, and name what failed. apiFetch resolves with {ok:false} rather
          // than throwing, so `await Promise.all(...)` succeeds even when every accept
          // was refused. The results used to be discarded outright: a candidate rejected
          // with 422 ("ENUM rule needs a non-empty 'values' array") or 500 vanished, the
          // stepper advanced, and the operator believed all N controls were bound when
          // some — or none — were. Accept is idempotent, so blocking here is safe to retry.
          const results = await Promise.all(
            documentCandidateIds.map(async (cid) => ({ cid, res: await api.acceptCandidate(cid, bindScope) }))
          );
          const rejected = results.filter(({ res }) => !res?.ok);
          if (rejected.length > 0) {
            const detail = rejected
              .map(({ cid, res }) => `“${byId.get(cid)?.proposedRuleName || 'Control'}” — ${res?.error || 'accept failed'}`)
              .join('; ');
            setError(
              `${rejected.length} of ${documentCandidateIds.length} controls could not be bound: ${detail}. `
              + 'Deselect them or complete their configuration, then continue.'
            );
            return;
          }
        }
        if (patternUuidsToAccept.length > 0) {
          const pr = await api.acceptPatternsAsRules(skillId, patternUuidsToAccept);
          if (!pr?.ok) {
            setError(`Pattern-sourced controls could not be bound: ${pr?.error || 'accept failed'}`);
            return;
          }
        }
      }
      setCurrentStep(3);
    } catch (e) { setError(e.message); }
    finally { setProcessing(false); }
  };

  // ── Step 4: Save binding config ─────────────────────────
  const handleSaveBinding = async () => {
    setError(null);
    setProcessing(true);
    try {
      if (configId) {
        await api.updatePolicyConfig(configId, {
          bindingConfig,
          selectedEnforcementIds: Array.from(selectedIds),
        });
      }
      setCurrentStep(4);
    } catch (e) { setError(e.message); }
    finally { setProcessing(false); }
  };

  // ── Step 5: Activate ───────────────────────────────────
  //
  // Two paths:
  //
  //  (A) Framework-context flow (unified pipeline, spec stage 5):
  //      Single transactional call to /api/v1/policy/activate that
  //      writes rules + bindings + attestation tasks + advances the
  //      FrameworkBinding state + records the immutable activation
  //      snapshot — all-or-nothing. Used whenever the wizard launched
  //      with a frameworkBindingId.
  //
  //  (B) Legacy flow:
  //      Pattern-only / document-only / direct-authoring wizards still
  //      use the older per-step endpoints. Eventually these collapse into
  //      path (A) but for May 15 they're kept working untouched.
  //
  // Either path calls onComplete() on success.
  const handleActivate = async (status = 'active') => {
    setError(null);
    setProcessing(true);
    try {
      // ── Path (A) — Unified pipeline ─────────────────────────────
      if (frameworkBindingId) {
        // Build the Stage-5 ActivateRequest from selected candidates.
        // Candidate field names match what handleExtract produced —
        // proposedRuleName / proposedRuleType / sourcePassage etc.
        // Attestation candidates carry the original control row in
        // c._attestationCandidate; we forward it intact so the backend
        // can resolve frameworkId from the controlId.
        const selected = candidates.filter(c => selectedIds.has(c.id));
        const rules = selected
          .filter(c => c._source !== 'framework-attestation')
          .map(c => ({
            name: c.proposedRuleName || c.name || 'Untitled rule',
            description: c.sourcePassage || c.description,
            ruleType: c.proposedRuleType || c.ruleType || 'EXPRESSION',
            severity: c.proposedSeverity || c.severity || 'WARN',
            ruleConfig: c.ruleConfig || {},
            source: { type: c._source === 'framework' ? 'FRAMEWORK'
                     : c._source === 'pattern' ? 'PATTERN'
                     : c._source === 'document' ? 'DOCUMENT' : 'DIRECT',
                     ref: c.patternId || c.documentId || null },
            frameworkControlMappings: c._frameworkControlMappings || c.frameworkControlMappings || [],
          }));
        const attestations = selected
          .filter(c => c._source === 'framework-attestation')
          .map(c => {
            const a = c._attestationCandidate || {};
            return {
              controlId: a.controlId,
              controlCode: a.controlCode,
              controlName: a.controlName,
              attestationFrequency: a.attestationFrequency,
            };
          });

        const sourceDescriptors = [];
        if (effectiveFrameworkId) sourceDescriptors.push({ type: 'FRAMEWORK', ref: effectiveFrameworkId, label: effectiveFrameworkName });
        if (selectedPatterns?.length) sourceDescriptors.push({ type: 'PATTERN', refs: selectedPatterns });
        if (documentIds?.length) sourceDescriptors.push({ type: 'DOCUMENT', refs: documentIds });

        // Resolve target agents.
        //   • AGENT scope with exactly 1 → agentId only
        //   • AGENT scope with N>1 → agentIds (multi-agent)
        //   • ORG/TENANT scope → no agent ids; backend fans out attestations
        //                        across the tenant (cap 200) and creates
        //                        scope-level bindings.
        const isAgentScope = bindingConfig.scope === 'AGENT';
        const pickedAgentIds = bindingConfig.agentIds || [];
        const body = {
          frameworkBindingId,
          agentId: isAgentScope && pickedAgentIds.length === 1 ? pickedAgentIds[0] : null,
          agentIds: isAgentScope && pickedAgentIds.length > 1 ? pickedAgentIds : null,
          skillId: skillId || null,
          // The operator's Enforcement Scope choice. Distinct from enforcementPoints: scope is WHO
          // the binding covers (TENANT / AGENT / ORG), the PEPs are WHERE it fires. The backend used
          // to receive no scope at all and wrote a PEP identifier into the scope column instead,
          // which the CHECK constraint rejected — killing every activation.
          scope: bindingConfig.scope || null,
          enforcementPoints: bindingConfig.enforcementPoints || [],
          rules,
          attestations,
          sourceDescriptors,
          reason: 'Activated via unified policy assignment wizard',
          // attestationAssignee is null today — backend defaults to the
          // activating user. When the role-based assignment surface lands
          // (Phase 7), the wizard will surface a per-task assignee picker.
        };

        const res = await api.activatePolicyAssignment(body);
        if (res.ok) {
          setBindingStatus('ACTIVE');
          const d = res.data || {};
          const nRules = (d.ruleIds || d.rules || []).length || rules.length;
          const nBindings = (d.bindingIds || []).length;
          const nAttest = (d.attestationTaskIds || d.attestationIds || []).length || attestations.length;
          setOutcome({
            ok: true,
            title: status === 'testing' ? 'Testing mode enabled' : 'Policy skill activated',
            detail: status === 'testing'
              ? 'Rules are evaluating against live traffic but will not block. Review the decisions, then activate fully.'
              : 'These controls are now enforced at the configured PEP checkpoints, before any LLM execution.',
            counts: [
              { label: 'Enforcement rules', value: nRules },
              ...(nBindings ? [{ label: 'Runtime bindings', value: nBindings }] : []),
              ...(nAttest ? [{ label: 'Attestation tasks', value: nAttest }] : []),
              ...(effectiveFrameworkName ? [{ label: 'Framework', value: effectiveFrameworkName }] : []),
            ],
            data: d,
          });
          return;
        } else {
          setError(res.error || 'Activation failed');
          setOutcome({
            ok: false,
            title: status === 'testing' ? 'Could not enable testing mode' : 'Activation failed',
            detail: res.error || 'Activation failed',
            counts: [],
          });
          return;
        }
      }

      // ── Path (B) — Legacy flow (no framework context) ───────────
      if (configId) {
        const res = await api.updatePolicyConfigStatus(configId, status);
        if (res.ok) {
          // Same terminal report as the framework path. The operator has just put controls
          // into production; "the panel closed" is not an acceptable confirmation.
          setOutcome({
            ok: true,
            title: status === 'testing' ? 'Testing mode enabled' : 'Policy skill activated',
            detail: status === 'testing'
              ? 'Rules are evaluating against live traffic but will not block. Review the decisions, then activate fully.'
              : 'These controls are now enforced at the configured PEP checkpoints, before any LLM execution.',
            counts: [{ label: 'Enforcement rules', value: selectedIds.size }],
            data: res.data,
          });
        } else {
          setError(res.error || 'Failed to activate');
          setOutcome({
            ok: false,
            title: status === 'testing' ? 'Could not enable testing mode' : 'Activation failed',
            detail: res.error || 'Failed to activate',
            counts: [],
          });
        }
      }
    } catch (e) { setError(e.message); }
    finally { setProcessing(false); }
  };

  /**
   * Centralised close handler. The wizard's stage transitions mutate
   * server state (PolicyConfig row, FrameworkBinding state machine,
   * dry-run testing mode). If the operator tries to leave with mutations
   * outstanding, we:
   *
   *   1. Confirm they really want to abandon — explain that testing mode
   *      remains on the server.
   *   2. Best-effort revert the FrameworkBinding state machine back from
   *      TESTING when applicable, so we don't leave dangling state.
   *
   * Rule of thumb: better to over-warn than to silently leave the framework
   * binding stuck in a half-applied state.
   */
  const handleClose = async () => {
    const isOnActivateStage = currentStep === 4;
    const hasMutatedTestingState = !!dryRunResults || bindingStatus === 'TESTING';
    if (isOnActivateStage && hasMutatedTestingState) {
      const proceed = window.confirm(
        'Testing mode is enabled on the server.\n\n' +
        'Cancelling the wizard now will leave the framework binding in TESTING ' +
        'with no rules behind it. You should either:\n' +
        '  • Click Activate to commit the rules, OR\n' +
        '  • Cancel here (we will attempt to revert testing mode).\n\n' +
        'Cancel anyway?'
      );
      if (!proceed) return;
      // Best-effort revert. Server may reject reverse transitions on
      // older state machines — we log and continue either way so the
      // operator isn't trapped.
      if (frameworkBindingId) {
        try {
          await api.ccAdvanceFrameworkBinding(frameworkBindingId, { targetStatus: 'RULES_GENERATED' });
          console.info('[Provenance] Reverted framework binding from TESTING to RULES_GENERATED on cancel.');
        } catch (e) {
          console.warn('[Provenance] Could not auto-revert framework binding on cancel:', e?.message || e);
        }
      }
    }
    onCancel();
  };

  const handleDryRun = async () => {
    setProcessing(true);
    try {
      // Activate in testing mode first
      if (configId) {
        const res = await api.updatePolicyConfigStatus(configId, 'testing');
        if (res.ok) {
          // Advance FrameworkBinding to TESTING
          if (frameworkBindingId) {
            try {
              await api.ccAdvanceFrameworkBinding(frameworkBindingId, { targetStatus: 'TESTING' });
              setBindingStatus('TESTING');
            } catch (e) { console.warn('[Provenance] Failed to advance binding to TESTING:', e); }
          }
          setDryRunResults({ status: 'testing', message: 'Policy skill is now in testing mode. Rules are evaluated but not enforced.' });
        }
      }
    } catch (e) { setError(e.message); }
    finally { setProcessing(false); }
  };

  const toggleCandidate = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(candidates.map(c => c.id)));
  const selectNone = () => setSelectedIds(new Set());
  const selectHighConfidence = () => {
    setSelectedIds(new Set(candidates.filter(c => c.confidenceScore >= 75).map(c => c.id)));
  };

  // Continue is blocked while work is in flight, and — on Stage 1 — until the
  // operator has actually chosen a source. Declared here (not beside
  // sourceSelection) because it reads autoCreating, which is initialised below
  // that block; a const referenced before its declaration is a TDZ crash.
  const continueBlocked = processing || autoCreating || (currentStep === 0 && !sourceSelection.complete);

  // Where "Close" sends the operator. When the wizard was opened from an agent's skill
  // (scopeAgentId), it returns them to THAT skill — the place they left — rather than dumping
  // them on the policy list and making them navigate back by hand.
  const originAgentName = scopeAgentId
    ? (resolvedAgents.find(a => String(a.id) === String(scopeAgentId))?.name || null)
    : null;
  const returnLabel = originAgentName ? `Back to ${originAgentName}` : 'Close';

  return (
    <div style={{
      padding: embeddedInSlideout ? '8px 4px 28px' : '28px 32px',
      maxWidth: embeddedInSlideout ? '100%' : 1280,
      margin: '0 auto',
    }}>
      {/* Header — G441: suppressed when hosted inside a V2 Slideout; the
          slideout title bar already provides the close affordance + title. */}
      {!embeddedInSlideout && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <button onClick={handleClose} style={{
            display: 'flex', alignItems: 'center', gap: 4, border: 'none',
            background: 'none', color: STONE[500], fontSize: 13, cursor: 'pointer', padding: 0,
          }}>
            <ArrowLeft size={16} /> Back
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: STONE[900], margin: 0 }}>
              {config ? 'Edit Policy Skill' : 'New Policy Skill Configuration'}
            </h1>
          </div>
        </div>
      )}

      {/* Framework Binding Provenance Banner */}
      {frameworkBindingId && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', borderRadius: 10, marginBottom: 16,
          background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
          border: '1px solid #bfdbfe',
        }}>
          <Shield size={16} color="#1e40af" />
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#1e40af' }}>
              Framework Binding: {frameworkName || 'Unknown Framework'}
            </span>
            <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
              Provenance tracked — {bindingStatus || 'PENDING'}
            </span>
          </div>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
            background: bindingStatus === 'ACTIVE' ? '#dcfce7' : bindingStatus === 'TESTING' ? '#fef3c7' : '#e0e7ff',
            color: bindingStatus === 'ACTIVE' ? '#166534' : bindingStatus === 'TESTING' ? '#92400e' : '#3730a3',
          }}>
            {bindingStatus || 'PENDING'}
          </span>
        </div>
      )}

      {/* Step Indicator */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32,
        padding: '16px 20px', background: STONE[50], borderRadius: 12,
      }}>
        {STEPS.map((step, i) => {
          const isActive = i === currentStep;
          const isDone = i < currentStep;
          const StepIcon = step.icon;
          return (
            <React.Fragment key={step.key}>
              {i > 0 && <div style={{ flex: 1, height: 2, background: isDone ? BRAND.primary : STONE[200], margin: '0 10px', minWidth: 20, maxWidth: 80 }} />}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                opacity: isActive ? 1 : isDone ? 0.9 : 0.4,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: isActive ? BRAND.primary : isDone ? BRAND.light : STONE[100],
                  color: isActive ? '#fff' : isDone ? BRAND.primary : STONE[400],
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}>
                  {isDone ? <Check size={14} /> : <StepIcon size={14} />}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: isActive ? 650 : 500, color: isActive ? STONE[900] : STONE[600] }}>
                    {step.label}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Error Banner */}
      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, background: '#fef2f2',
          border: '1px solid #fecaca', color: '#991b1b', fontSize: 13,
          marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={14} /> {error}
          <button onClick={() => setError(null)} style={{
            marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: '#991b1b',
          }}><X size={14} /></button>
        </div>
      )}

      {/* Step Content */}
      <div style={{
        background: '#fff', border: `1px solid ${STONE[200]}`, borderRadius: 14,
        padding: 28, minHeight: 400,
      }}>
        <div style={{ fontSize: 12, color: STONE[500], marginBottom: 20 }}>
          {STEPS[currentStep].desc}
        </div>

        {currentStep === 0 && (
          <StepSource
            skillId={skillId} setSkillId={setSkillId}
            materialized={materialized}
            documentIds={documentIds} setDocumentIds={setDocumentIds}
            skillDocs={skillDocs} setSkillDocs={setSkillDocs}
            selectedPatterns={selectedPatterns} setSelectedPatterns={setSelectedPatterns}
            sourceModes={sourceModes} setSourceModes={setSourceModes}
            skills={skills}
            isAgentFiltered={targetAgentIds.length > 0}
            targetAgentName={targetAgentIds.length > 0 && agents ? agents.find(a => targetAgentIds.includes(a.id))?.name : null}
            autoCreatedSkill={autoCreatedSkill}
            autoCreating={autoCreating}
            // Framework context: effective values (prop-supplied or
            // user-picked from the in-wizard dropdown). Setters let
            // StepSource update the picked framework when the operator
            // selects one inline.
            frameworkId={effectiveFrameworkId}
            frameworkName={effectiveFrameworkName}
            isFrameworkPreset={!!frameworkId}
            onPickFramework={(id, name) => {
              setPickedFrameworkId(id);
              setPickedFrameworkName(name);
            }}
          />
        )}

        {currentStep === 1 && (
          <StepProcessing
            documentIds={documentIds}
            selectedPatterns={selectedPatterns}
            sourceModes={sourceModes}
            processing={processing}
            candidates={candidates}
            onExtract={handleExtract}
            frameworkId={effectiveFrameworkId}
            frameworkName={effectiveFrameworkName}
          />
        )}

        {currentStep === 2 && (
          <>
            <ScopeChoice value={bindScope} onChange={setBindScope} />
            <StepReview
              candidates={candidates}
              selectedIds={selectedIds}
              onToggle={toggleCandidate}
              onSelectAll={selectAll}
              onSelectNone={selectNone}
              onSelectHighConf={selectHighConfidence}
            />
          </>
        )}

        {currentStep === 3 && (
          <StepBinding
            bindingConfig={bindingConfig}
            setBindingConfig={setBindingConfig}
            agents={resolvedAgents}
            selectedCount={selectedIds.size}
            materialized={materialized}
            scopeAgentId={scopeAgentId}
            onStatsChange={setBindStats}
            // Unified pipeline (spec: docs/UNIFIED_POLICY_PIPELINE_SPEC.md):
            // pass framework context for the coverage matrix and the
            // selected candidates for the rule/attestation split.
            frameworkBindingId={frameworkBindingId}
            frameworkId={effectiveFrameworkId}
            frameworkName={effectiveFrameworkName}
            candidates={candidates}
            selectedIds={selectedIds}
          />
        )}

        {currentStep === 4 && (
          outcome ? (
            <ActivationOutcome
              outcome={outcome}
              returnLabel={returnLabel}
              onClose={() => { const d = outcome.data; setOutcome(null); onComplete(d); }}
              onRetry={() => setOutcome(null)}
            />
          ) : (
            <StepActivation
              selectedCount={selectedIds.size}
              inheritedCount={bindStats.inheritedCount}
              applicablePepCount={bindStats.applicablePepCount}
              dryRunResults={dryRunResults}
              onDryRun={handleDryRun}
              processing={processing}
            />
          )
        )}
      </div>

      {/* Navigation Buttons — suppressed on the terminal outcome screen, which owns its own
          Close / Back-to-review actions. Leaving "Activate Policy Skill" live underneath a
          success panel invites a second activation of the same thing. */}
      {!outcome && (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <button
          onClick={() => {
            // Back is purely client-side navigation — no server rollback.
            // BUT certain prior steps have already mutated server state
            // (PolicyConfig row, FrameworkBinding state machine, dry-run
            // testing mode). On Stage 5 specifically, leaving without
            // either activating or reverting would strand the framework
            // binding in TESTING with no rules behind it. Guard with a
            // confirm so operators understand what's happening.
            if (currentStep === 0) { handleClose(); return; }
            const isOnActivateStage = currentStep === 4;
            const hasMutatedTestingState = !!dryRunResults || bindingStatus === 'TESTING';
            if (isOnActivateStage && hasMutatedTestingState) {
              const proceed = window.confirm(
                'Heads up — testing mode is enabled on the server.\n\n' +
                "Going back won't undo it. The framework binding will stay " +
                'in TESTING until you either:\n' +
                '  • activate from this step, OR\n' +
                '  • cancel the wizard (we will attempt to revert testing mode).\n\n' +
                'Continue back to edit the binding configuration?'
              );
              if (!proceed) return;
            }
            const target = currentStep - 1;
            // Going back into Source Selection (0) or Extract (1) means the operator
            // may change the source mix (documents / framework / patterns). Clear the
            // downstream extraction state so the Extract step re-shows its pre-run
            // "Start Extraction" reflecting the CURRENT sources, and Review never shows
            // stale candidates/selection from a prior source mix (the inconsistency
            // where Back made documents "disappear" and only framework rules remained).
            if (target <= 1) {
              setCandidates([]);
              setSelectedIds(new Set());
            }
            setCurrentStep(target);
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
            fontSize: 13, fontWeight: 500, borderRadius: 8,
            border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[600],
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={14} /> {currentStep === 0 ? 'Cancel' : 'Back'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Save & Exit — each Continue already persists this stage server-side
              (config, extracted candidates, accepted selection, binding), so
              closing here is non-destructive: reopen from the Policies list to
              resume exactly where you left off. Hidden until a config exists. */}
          {(configId || skillId) && currentStep < 4 && (
            <button
              type="button"
              onClick={() => { if (onCancel) onCancel(); }}
              title="Your progress on completed steps is saved. Reopen this policy from the Policies list to resume."
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                fontSize: 13, fontWeight: 500, borderRadius: 8,
                border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[600], cursor: 'pointer',
              }}
            >
              Save &amp; Exit
            </button>
          )}

          <button
            onClick={() => {
              if (currentStep === 0) handleCreateConfig();
              else if (currentStep === 1) handleExtract();
              else if (currentStep === 2) handleAcceptSelected();
              else if (currentStep === 3) handleSaveBinding();
              else if (currentStep === 4) handleActivate('active');
            }}
            disabled={continueBlocked}
            title={currentStep === 0 && !sourceSelection.complete
              ? (sourceSelection.emptyEnabledSources.length > 0
                  ? sourceSelection.emptyEnabledSources.join('; ')
                  : 'Select at least one document, pattern, or framework control to continue')
              : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px',
              fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none',
              background: continueBlocked ? STONE[400] : BRAND.primary,
              color: '#fff', cursor: continueBlocked ? 'not-allowed' : 'pointer',
            }}
          >
            {processing && <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            {currentStep === 4 ? 'Activate Policy Skill' : (currentStep === 0 && !skillId && !autoCreatedSkill && frameworkBindingId ? 'Create Policy Skill & Continue' : 'Continue')}
            {currentStep < 4 && <ArrowRight size={14} />}
          </button>
        </div>
      </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ─── Terminal outcome of stage 5 ──────────────────────────────
 *
 * Activation is the moment controls start (or stop) blocking production traffic. It is the
 * single most consequential action in this wizard, and it used to report itself by making the
 * panel disappear — no confirmation, no summary, and the operator dumped on a policy list
 * rather than back on the agent skill they came from.
 *
 * This screen states plainly what was written, and returns the operator to their origin.
 */
function ActivationOutcome({ outcome, returnLabel, onClose, onRetry }) {
  const ok = outcome.ok;
  const accent = ok ? BRAND.primary : '#b91c1c';
  const wash = ok ? '#e3f4f1' : '#fef2f2';
  return (
    <div style={{
      padding: '28px 24px', borderRadius: 12, background: wash,
      border: `1px solid ${accent}33`, textAlign: 'center',
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%', background: accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
      }}>
        {ok ? <Check size={22} color="#fff" /> : <AlertTriangle size={22} color="#fff" />}
      </div>

      <div style={{ fontSize: 17, fontWeight: 700, color: STONE[900], marginBottom: 6 }}>
        {outcome.title}
      </div>
      <div style={{ fontSize: 13, color: STONE[600], maxWidth: 560, margin: '0 auto 18px', lineHeight: 1.5 }}>
        {outcome.detail}
      </div>

      {outcome.counts?.length > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 28, flexWrap: 'wrap',
          padding: '14px 18px', borderRadius: 10, background: '#fff',
          border: `1px solid ${STONE[200]}`, margin: '0 auto 20px', width: 'fit-content',
        }}>
          {outcome.counts.map((c) => (
            <div key={c.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: STONE[900] }}>{c.value}</div>
              <div style={{ fontSize: 11, color: STONE[500], textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {c.label}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        {!ok && (
          <button onClick={onRetry} style={{
            padding: '10px 18px', fontSize: 13, fontWeight: 500, borderRadius: 8,
            border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[700], cursor: 'pointer',
          }}>
            Back to review
          </button>
        )}
        <button onClick={onClose} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '10px 20px', fontSize: 13, fontWeight: 600, borderRadius: 8,
          border: 'none', background: accent, color: '#fff', cursor: 'pointer',
        }}>
          <ArrowLeft size={14} /> {returnLabel}
        </button>
      </div>
    </div>
  );
}

/* ─── Step 1: Source Selection ─────────────────────────────── */
//
// Spec: docs/UNIFIED_POLICY_PIPELINE_SPEC.md (Stage 1: SOURCE).
//
// Multi-select: any combination of {framework, patterns, documents} can
// be enabled in one extraction pass. When a framework binding context is
// present (frameworkId set), framework is enabled by default — the wizard
// was opened with the explicit intent to bind to that framework, so the
// framework controls panel is always visible. Patterns and Documents are
// purely additive: enabling them adds candidates to the same flat list
// the review step renders.
// Shared "Currently enforcing" panel — rendered on both the Source and
// Configure-Binding steps so re-configuring is never a blind restart. Returns
// null when nothing is materialized yet (clean-slate skill).
function EnforcingPanel({ materialized }) {
  if (!materialized || materialized.length === 0) return null;
  return (
    <div style={{ border: '1px solid #e7e5e4', borderRadius: 8, padding: 12, background: '#fafaf9' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: STONE[700], marginBottom: 4 }}>
        Currently enforcing ({materialized.filter(m => m.enabled).length})
      </div>
      <div style={{ fontSize: 12, color: STONE[500], marginBottom: 8, lineHeight: 1.5 }}>
        This policy skill already applies these controls. Saving here replaces them — anything you don&rsquo;t keep is retired automatically.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {materialized.map((m) => (
          <span key={m.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
            padding: '2px 8px', borderRadius: 999,
            background: m.enabled ? '#ecfdf5' : '#f5f5f4',
            color: m.enabled ? '#065f46' : STONE[500],
            border: `1px solid ${m.enabled ? '#a7f3d0' : '#e7e5e4'}`,
          }}>
            {m.name}{m.enabled ? '' : ' · off'}
          </span>
        ))}
      </div>
    </div>
  );
}

function StepSource({
  skillId, setSkillId,
  materialized,
  documentIds, setDocumentIds,
  // skillDocs is owned by the wizard shell (lifted) so the Extract stage can
  // name a document in a failure message instead of echoing its UUID, and so
  // the Continue guard can reason about what is actually selectable.
  skillDocs, setSkillDocs,
  selectedPatterns, setSelectedPatterns,
  sourceModes, setSourceModes,
  skills, isAgentFiltered, targetAgentName,
  autoCreatedSkill, autoCreating,
  // frameworkId/Name = effective values (prop-supplied OR user-picked).
  // isFrameworkPreset = true when the wizard was opened from a Framework
  // Coverage Panel handoff (frameworkId came in via props). When false,
  // the operator picks a framework inline from the dropdown rendered
  // beneath the Framework Controls tile.
  frameworkId, frameworkName, isFrameworkPreset, onPickFramework,
}) {
  const isModeOn = (m) => sourceModes.includes(m);
  const toggleMode = (m) => {
    setSourceModes(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };

  // ── Framework catalogue (for inline picker) ──
  // Loaded once when the framework source is enabled and no framework is
  // pre-selected. We use the existing ccListFrameworks endpoint (already
  // used by the Framework Coverage Panel) so backend surface area is unchanged.
  const [frameworkCatalogue, setFrameworkCatalogue] = useState([]);
  const [loadingCatalogue, setLoadingCatalogue] = useState(false);
  useEffect(() => {
    if (!isModeOn('framework') || isFrameworkPreset) return;
    if (frameworkCatalogue.length > 0) return;
    let cancelled = false;
    setLoadingCatalogue(true);
    (async () => {
      try {
        const res = await api.ccListFrameworks({});
        if (cancelled) return;
        if (res.ok) {
          const list = Array.isArray(res.data) ? res.data : (res.data?.frameworks || []);
          setFrameworkCatalogue(list);
        }
      } catch (_) { /* swallow */ }
      finally { if (!cancelled) setLoadingCatalogue(false); }
    })();
    return () => { cancelled = true; };
  }, [sourceModes.join(','), isFrameworkPreset, frameworkCatalogue.length]);

  // ── Framework Controls preview ──
  // Fetched whenever the framework source is enabled AND a framework is
  // resolved (either preset or just-picked from the dropdown).
  const [frameworkControls, setFrameworkControls] = useState(null);
  const [loadingFrameworkControls, setLoadingFrameworkControls] = useState(false);
  useEffect(() => {
    if (!isModeOn('framework') || !frameworkId) { setFrameworkControls(null); return; }
    let cancelled = false;
    setLoadingFrameworkControls(true);
    (async () => {
      try {
        const res = await api.listFrameworkControlsAsSource(frameworkId);
        if (cancelled) return;
        if (res.ok) setFrameworkControls(res.data);
      } catch (_) { /* swallow — section just stays empty */ }
      finally { if (!cancelled) setLoadingFrameworkControls(false); }
    })();
    return () => { cancelled = true; };
  }, [sourceModes.join(','), frameworkId]);

  const [loadingDocs, setLoadingDocs] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Auto-fetch documents when skill is selected
  useEffect(() => {
    if (!skillId) { setSkillDocs([]); return; }
    let cancelled = false;
    (async () => {
      setLoadingDocs(true);
      try {
        const res = await api.listPolicyDocuments(skillId);
        if (!cancelled && res.ok) {
          const docs = res.data || [];
          setSkillDocs(docs);
          const validDocIds = new Set(docs.map(d => d.id));
          // Clean stale document IDs that no longer exist on the skill
          setDocumentIds(prev => {
            const cleaned = prev.filter(id => validDocIds.has(id));
            // If nothing survived cleanup, auto-select all parsed documents
            if (cleaned.length === 0) {
              const parsedIds = docs.filter(d => d.status === 'parsed' || d.status === 'ready').map(d => d.id);
              return parsedIds;
            }
            return cleaned;
          });
        }
      } catch (e) { console.error('Failed to load skill documents', e); }
      finally { if (!cancelled) setLoadingDocs(false); }
    })();
    return () => { cancelled = true; };
  }, [skillId, refreshKey]);

  const toggleDoc = (docId) => {
    setDocumentIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleDeleteDoc = async (e, docId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this document and all its chunks & extracted candidates? This cannot be undone.')) return;
    setDeletingDocId(docId);
    try {
      await api.deletePolicyDocument(skillId, docId);
      // Remove from selected IDs
      setDocumentIds(prev => prev.filter(id => id !== docId));
      // Refresh document list
      setRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Failed to delete document', err);
      alert('Failed to delete document: ' + err.message);
    } finally {
      setDeletingDocId(null);
    }
  };

  // ── Pattern loading ──
  const [patterns, setPatterns] = useState([]);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [patternSearch, setPatternSearch] = useState('');
  const [patternDomainFilter, setPatternDomainFilter] = useState('');

  useEffect(() => {
    // Only fetch the pattern catalogue when the patterns source is enabled.
    if (!isModeOn('patterns')) return;
    let cancelled = false;
    (async () => {
      setLoadingPatterns(true);
      try {
        const res = await api.getEnforcementPatterns(patternDomainFilter || undefined);
        if (!cancelled && res.ok) setPatterns(Array.isArray(res.data) ? res.data : res.data?.patterns || []);
      } catch (_) {}
      finally { if (!cancelled) setLoadingPatterns(false); }
    })();
    return () => { cancelled = true; };
  }, [sourceModes.join(','), patternDomainFilter]);

  const togglePattern = (pattern) => {
    setSelectedPatterns(prev => {
      const exists = prev.find(p => p.id === pattern.id);
      return exists ? prev.filter(p => p.id !== pattern.id) : [...prev, pattern];
    });
  };
  const isPatternSelected = (id) => selectedPatterns.some(p => p.id === id);

  const filteredPatterns = patterns.filter(p => {
    if (!patternSearch) return true;
    const q = patternSearch.toLowerCase();
    return (p.patternName || '').toLowerCase().includes(q) ||
           (p.domain || '').toLowerCase().includes(q) ||
           (p.mapsToRuleType || '').toLowerCase().includes(q);
  });

  // ── Source mode tiles (multi-select) ──
  // Spec: docs/UNIFIED_POLICY_PIPELINE_SPEC.md §"Stage 1: SOURCE".
  // Each tile toggles inclusion in the extraction pass. All three sources
  // are always available — when the framework tile is enabled without a
  // preset framework, the operator picks one from the inline dropdown
  // that appears beneath the tiles.
  const sourceTiles = [
    {
      key: 'framework',
      label: 'Framework Controls',
      icon: Shield,
      desc: frameworkId
        ? `${frameworkName || 'Framework'}'s automatable controls become rules; manual controls become attestation tasks.`
        : 'Bind directly to a regulatory framework. Pick a framework below; automatable controls become rules, manual controls become attestation tasks.',
    },
    { key: 'patterns', label: 'Pattern Templates', icon: Layers, desc: 'Select reusable enforcement patterns from the library' },
    { key: 'documents', label: 'Policy Documents', icon: FileText, desc: 'Extract rules from uploaded policy documents using RAG' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Currently enforcing — what this policy skill already applies, so this isn't a blind restart. */}
      <EnforcingPanel materialized={materialized} />

      {/* Policy Skill Selector */}
      <div>
        <label style={{ fontSize: 13, fontWeight: 600, color: STONE[700], display: 'block', marginBottom: 6 }}>
          Policy Skill *
        </label>
        {autoCreating ? (
          /* Auto-creating policy skill — show loading spinner */
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            border: `1.5px solid #60a5fa`, background: '#eff6ff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <RefreshCw size={15} color="#2563eb" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>
                Creating Policy Skill{targetAgentName ? ` for "${targetAgentName}"` : ''}...
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.5 }}>
              Automatically creating a new policy skill to bind framework controls to this agent.
            </div>
          </div>
        ) : autoCreatedSkill ? (
          /* Auto-created successfully — show success message + dropdown with skill pre-selected */
          <div>
            <div style={{
              padding: '12px 16px', borderRadius: 10, marginBottom: 8,
              border: `1.5px solid #34d399`, background: '#ecfdf5',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Check size={15} color="#059669" />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#065f46' }}>
                  Policy Skill auto-created for {targetAgentName ? `"${targetAgentName}"` : 'agent'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#047857', marginTop: 4, lineHeight: 1.5 }}>
                <strong>{autoCreatedSkill.name}</strong> has been created and assigned. You can proceed with the binding workflow.
              </div>
            </div>
            <select value={skillId} onChange={e => setSkillId(e.target.value)} style={selectStyle}>
              {skills.length > 0 ? (
                skills.map(s => (
                  <option key={s.id} value={s.id}>{s.name} — {s.description?.substring(0, 60) || s.id}</option>
                ))
              ) : (
                <option value={autoCreatedSkill.id || skillId}>{autoCreatedSkill.name}</option>
              )}
            </select>
          </div>
        ) : isAgentFiltered && skills.length === 0 ? (
          /* Agent has no policy skills and no auto-create triggered — show warning */
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            border: `1.5px solid #fbbf24`, background: '#fffbeb',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <AlertTriangle size={15} color="#d97706" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                No Policy Skill found{targetAgentName ? ` for "${targetAgentName}"` : ' for this agent'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#78716c', lineHeight: 1.5 }}>
              This agent does not have a Policy Skill assigned. A new Policy Skill will be created as part of this binding workflow. Enter a skill ID below if you want to bind to a specific skill, or leave empty to auto-create.
            </div>
            <input
              value={skillId} onChange={e => setSkillId(e.target.value)}
              placeholder="Enter existing skill UUID or leave empty to auto-create..."
              style={{ ...inputStyle, marginTop: 10, background: '#fff' }}
            />
          </div>
        ) : skills.length > 0 ? (
          <select value={skillId} onChange={e => setSkillId(e.target.value)} style={selectStyle}>
            <option value="">Select a policy skill...</option>
            {skills.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}{s._agentName ? ` · ${s._agentName}` : ''} — {s.description?.substring(0, 60) || s.id}
              </option>
            ))}
          </select>
        ) : (
          <input
            value={skillId} onChange={e => setSkillId(e.target.value)}
            placeholder="Enter skill UUID..." style={inputStyle}
          />
        )}
        <div style={{ fontSize: 11, color: autoCreatedSkill ? '#065f46' : isAgentFiltered ? '#065f46' : STONE[400], marginTop: 4 }}>
          {autoCreatedSkill
            ? `Auto-created and selected: ${autoCreatedSkill.name}`
            : isAgentFiltered
              ? skills.length > 0
                ? `Filtered: showing ${skills.length} policy skill(s) for the selected agent`
                : ''
              : 'Select the policy skill that will receive the enforcement rules'
          }
        </div>
      </div>

      {/* Source Mode Tiles (multi-select) */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: STONE[700] }}>
            Enforcement Sources
          </label>
          <span style={{ fontSize: 11, color: STONE[500] }}>
            {sourceModes.length === 0
              ? 'Pick one or more — they extract together in a single pass.'
              : `${sourceModes.length} source${sourceModes.length === 1 ? '' : 's'} enabled — candidates from all selected sources merge into the next review step.`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {sourceTiles.map(tile => {
            const active = isModeOn(tile.key);
            const ModeIcon = tile.icon;
            return (
              <button key={tile.key} onClick={() => toggleMode(tile.key)} style={{
                flex: 1, padding: '14px 16px', borderRadius: 10, border: `1.5px solid ${active ? BRAND.primary : STONE[200]}`,
                background: active ? BRAND.light : '#fff', cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.15s', position: 'relative',
              }}>
                {/* Multi-select check indicator — small box top-right shows include/exclude state. */}
                <div style={{
                  position: 'absolute', top: 10, right: 10,
                  width: 18, height: 18, borderRadius: 5,
                  border: `1.5px solid ${active ? BRAND.primary : STONE[200]}`,
                  background: active ? BRAND.primary : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {active && <Check size={12} color="#fff" strokeWidth={3} />}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, paddingRight: 24 }}>
                  <ModeIcon size={15} color={active ? BRAND.primary : STONE[500]} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: active ? BRAND.dark : STONE[700] }}>{tile.label}</span>
                </div>
                <div style={{ fontSize: 11, color: STONE[500], lineHeight: 1.4 }}>{tile.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Inline Framework Picker ────────────────────────────────────
            Shown when the framework source is enabled and no framework
            was preset (i.e. the wizard wasn't launched from the Coverage
            Panel handoff). Picking a framework here flows into the
            preview block and the Stage 2 extract call. */}
      {isModeOn('framework') && !isFrameworkPreset && (
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: STONE[700], display: 'block', marginBottom: 6 }}>
            Framework
          </label>
          <FrameworkPicker
            frameworks={frameworkCatalogue}
            value={frameworkId || ''}
            loading={loadingCatalogue}
            onChange={(id, name) => { if (onPickFramework) onPickFramework(id, name); }}
          />
          <div style={{ fontSize: 11, color: STONE[400], marginTop: 6 }}>
            Framework controls feed the extraction step directly — automatable controls become candidate rules,
            and manual controls become attestation tasks created when you activate the policy.
          </div>
        </div>
      )}

      {/* Documents Panel — visible whenever the documents source is enabled. */}
      {isModeOn('documents') && (
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: STONE[700], display: 'block', marginBottom: 6 }}>
            Policy Documents
          </label>

          {!skillId && (
            <div style={{ padding: '24px 16px', borderRadius: 10, background: STONE[50], border: `1px dashed ${STONE[200]}`, textAlign: 'center' }}>
              <FileText size={20} color={STONE[400]} style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 13, color: STONE[500] }}>Select a policy skill above to see its uploaded documents</div>
            </div>
          )}

          {skillId && loadingDocs && (
            <div style={{ padding: '20px', textAlign: 'center', color: STONE[400], fontSize: 13 }}>
              <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', marginBottom: 6 }} />
              <div>Loading documents...</div>
            </div>
          )}

          {skillId && !loadingDocs && skillDocs.length === 0 && (
            <div style={{ padding: '24px 16px', borderRadius: 10, background: '#e3f4f1', border: `1px dashed ${BRAND.primary}40`, textAlign: 'center' }}>
              <AlertTriangle size={20} color={BRAND.primary} style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 13, color: STONE[700], fontWeight: 600, marginBottom: 4 }}>No documents uploaded yet</div>
              <div style={{ fontSize: 12, color: STONE[500] }}>
                Go to Agent Lifecycle, open the agent with this policy skill, and use "Upload Document" on the skill card first.
              </div>
            </div>
          )}

          {skillId && !loadingDocs && skillDocs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, color: STONE[500], marginBottom: 4 }}>
                {documentIds.length} of {skillDocs.length} documents selected for enforcement extraction
              </div>
              {skillDocs.map(doc => {
                const selected = documentIds.includes(doc.id);
                const isDeleting = deletingDocId === doc.id;
                const displayName = doc.originalFilename || doc.filename || doc.id;
                return (
                  <div key={doc.id} onClick={() => !isDeleting && toggleDoc(doc.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', borderRadius: 10,
                    background: isDeleting ? '#fef2f2' : (selected ? BRAND.light : STONE[50]),
                    border: `1px solid ${isDeleting ? '#fecaca' : (selected ? BRAND.primary + '40' : STONE[200])}`,
                    cursor: isDeleting ? 'wait' : 'pointer', transition: 'all 0.15s',
                    opacity: isDeleting ? 0.6 : 1,
                  }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                      border: `2px solid ${selected ? BRAND.primary : STONE[400]}`,
                      background: selected ? BRAND.primary : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {selected && <Check size={12} color="#fff" />}
                    </div>
                    <FileText size={16} color={selected ? BRAND.primary : STONE[500]} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: STONE[900], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {displayName}
                      </div>
                      <div style={{ fontSize: 11, color: STONE[500], display: 'flex', gap: 12, marginTop: 2 }}>
                        <span style={{
                          padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          background: doc.status === 'parsed' ? '#dcfce7' : '#fef3c7',
                          color: doc.status === 'parsed' ? '#166534' : '#92400e',
                        }}>{doc.status}</span>
                        {doc.chunkCount != null && <span>{doc.chunkCount} chunks</span>}
                        {doc.classification && <span>{doc.classification}</span>}
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleDeleteDoc(e, doc.id)}
                      disabled={isDeleting}
                      title="Delete document, chunks, and extracted candidates"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: 6, border: 'none', flexShrink: 0,
                        background: 'transparent', color: STONE[400], cursor: isDeleting ? 'wait' : 'pointer',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = STONE[400]; }}
                    >
                      {isDeleting ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={14} />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 11, color: STONE[400], marginTop: 8 }}>
            Documents are uploaded on the skill card in Agent Lifecycle. Select which to include in enforcement extraction.
          </div>
        </div>
      )}

      {/* Pattern Templates Panel — visible whenever the patterns source is enabled. */}
      {isModeOn('patterns') && (
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: STONE[700], display: 'block', marginBottom: 6 }}>
            Pattern Templates
          </label>

          {/* Search + Domain Filter */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: STONE[400] }} />
              <input value={patternSearch} onChange={e => setPatternSearch(e.target.value)}
                placeholder="Search patterns by name, domain, or rule type..."
                style={{ ...inputStyle, paddingLeft: 30 }}
              />
            </div>
            <select value={patternDomainFilter} onChange={e => setPatternDomainFilter(e.target.value)} style={{ ...selectStyle, width: 200 }}>
              <option value="">All Domains</option>
              <option value="SECURITY">Security</option>
              <option value="ACCESS_CONTROL">Access Control</option>
              <option value="DATA_CLASSIFICATION">Data Classification</option>
              <option value="CHANGE_MANAGEMENT">Change Management</option>
              <option value="OPERATIONAL">Operational</option>
              <option value="COMPLIANCE">Compliance</option>
            </select>
          </div>

          {/* Selection summary */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
            background: BRAND.light, borderRadius: 8, marginBottom: 12, flexWrap: 'wrap',
          }}>
            <Layers size={14} color={BRAND.primary} />
            <span style={{ fontSize: 12, fontWeight: 600, color: BRAND.dark }}>
              {selectedPatterns.length > 0 ? `${selectedPatterns.length} pattern(s) selected` : 'No patterns selected'}
            </span>
            <div style={{ flex: 1 }} />
            {filteredPatterns.length > 0 && selectedPatterns.length < filteredPatterns.length && (
              <button onClick={() => {
                const allVisible = filteredPatterns.filter(p => p.enabled !== false);
                setSelectedPatterns(prev => {
                  const existingIds = new Set(prev.map(pp => pp.id));
                  const merged = [...prev];
                  allVisible.forEach(p => { if (!existingIds.has(p.id)) merged.push(p); });
                  return merged;
                });
              }} style={{
                fontSize: 11, color: BRAND.primary, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600,
              }}>Select All</button>
            )}
            {selectedPatterns.length > 0 && (
              <button onClick={() => setSelectedPatterns([])} style={{
                fontSize: 11, color: BRAND.primary, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600,
              }}>Clear All</button>
            )}
          </div>

          {/* Pattern List */}
          {loadingPatterns && (
            <div style={{ padding: '20px', textAlign: 'center', color: STONE[400], fontSize: 13 }}>
              <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', marginBottom: 6 }} />
              <div>Loading patterns...</div>
            </div>
          )}

          {!loadingPatterns && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto', paddingRight: 4 }}>
              {filteredPatterns.map(pat => {
                const selected = isPatternSelected(pat.id);
                const domColors = PATTERN_DOMAIN_COLORS[pat.domain] || PATTERN_DOMAIN_COLORS.COMPLIANCE;
                const DomIcon = PATTERN_DOMAIN_ICONS[pat.domain] || Shield;
                return (
                  <div key={pat.id} onClick={() => togglePattern(pat)} style={{
                    display: 'grid', gridTemplateColumns: '24px 1fr auto',
                    gap: 12, alignItems: 'center', padding: '12px 14px', borderRadius: 10,
                    border: `1px solid ${selected ? BRAND.primary + '40' : STONE[200]}`,
                    background: selected ? BRAND.light + '40' : '#fff',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                    <input type="checkbox" checked={selected} onChange={() => togglePattern(pat)}
                      style={{ width: 18, height: 18, accentColor: BRAND.primary, cursor: 'pointer' }}
                      onClick={e => e.stopPropagation()}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: STONE[900], marginBottom: 3 }}>
                        {pat.patternName}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          background: domColors.bg, color: domColors.text, border: `1px solid ${domColors.border}`,
                        }}>
                          <DomIcon size={10} /> {(pat.domain || '').replace(/_/g, ' ')}
                        </span>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 500,
                          background: STONE[100], color: STONE[600], fontFamily: "'IBM Plex Mono', monospace",
                        }}>{pat.mapsToRuleType}</span>
                        {pat.confidenceWeight != null && (
                          <span style={{ fontSize: 11, color: STONE[400] }}>
                            Weight: {Math.round(pat.confidenceWeight * 100)}%
                          </span>
                        )}
                      </div>
                      {pat.exampleText && (
                        <div style={{ fontSize: 11, color: STONE[500], marginTop: 4, lineHeight: 1.4,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                          {pat.exampleText.substring(0, 120)}{pat.exampleText.length > 120 ? '...' : ''}
                        </div>
                      )}
                    </div>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: pat.enabled !== false ? '#16a34a' : STONE[400],
                      padding: '3px 8px', borderRadius: 4,
                      background: pat.enabled !== false ? '#f0fdf4' : STONE[50],
                    }}>
                      {pat.enabled !== false ? 'ACTIVE' : 'DISABLED'}
                    </div>
                  </div>
                );
              })}
              {filteredPatterns.length === 0 && (
                <div style={{ textAlign: 'center', padding: 24, color: STONE[400], fontSize: 13 }}>
                  {patternSearch ? 'No patterns match your search' : 'No patterns available'}
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: 11, color: STONE[400], marginTop: 8 }}>
            Patterns are reusable enforcement templates. Selected patterns will generate enforcement candidates with pre-configured rules.
          </div>
        </div>
      )}

      {/* ── Framework Controls preview ─────────────────────────────────
            Visible whenever the framework source is enabled. Fetches the
            framework's controls via api.listFrameworkControlsAsSource()
            and previews the automatable / manual split. The actual
            candidate generation happens in Stage 2 — this is the
            operator's sanity-check.
        ───────────────────────────────────────────────────────────────── */}
      {isModeOn('framework') && frameworkId && (
        <div style={{
          border: `1px solid ${STONE[200]}`, borderRadius: 12, padding: 20,
          background: '#fff',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: STONE[800], marginBottom: 6,
            display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={15} color="#7c3aed" />
            Framework Controls — {frameworkName || 'Framework'}
          </div>
          {loadingFrameworkControls && (
            <div style={{ fontSize: 12, color: STONE[500] }}>
              <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite', verticalAlign: '-2px', marginRight: 6 }} />
              Loading controls…
            </div>
          )}
          {!loadingFrameworkControls && frameworkControls && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div style={{
                  padding: '12px 14px', borderRadius: 10,
                  background: '#ecfdf5', border: '1px solid #a7f3d0',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#065f46',
                    textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Automatable → candidate rules
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#064e3b', marginTop: 4 }}>
                    {frameworkControls.automatableCount}
                  </div>
                  <div style={{ fontSize: 11, color: '#065f46', marginTop: 2 }}>
                    Rules will be auto-generated in the next step.
                  </div>
                </div>
                <div style={{
                  padding: '12px 14px', borderRadius: 10,
                  background: '#fffbeb', border: '1px solid #fde68a',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e',
                    textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Manual → attestation tasks
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#78350f', marginTop: 4 }}>
                    {frameworkControls.manualCount}
                  </div>
                  <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
                    Attestation tasks will be created in Compliance Center.
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: STONE[500], lineHeight: 1.5 }}>
                Total <strong>{frameworkControls.totalControls}</strong> control{frameworkControls.totalControls !== 1 ? 's' : ''}
                . You'll review and select which ones to apply in the next steps.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Step 2: Processing ───────────────────────────────────── */
function StepProcessing({ documentIds, selectedPatterns, sourceModes, processing, candidates, onExtract, frameworkId, frameworkName }) {
  const hasDocuments = sourceModes.includes('documents') && documentIds.length > 0;
  const hasPatterns = sourceModes.includes('patterns') && selectedPatterns.length > 0;
  const hasFramework = sourceModes.includes('framework') && !!frameworkId;
  const docCount = hasDocuments ? documentIds.length : 0;
  const patCount = hasPatterns ? selectedPatterns.length : 0;

  const sourceDesc = hasFramework
    ? `framework: ${frameworkName || 'controls'}`
    : hasDocuments && hasPatterns
      ? `${docCount} document(s) + ${patCount} pattern template(s)`
      : hasDocuments ? `${docCount} document(s)` : `${patCount} pattern template(s)`;

  return (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      {candidates.length === 0 && !processing && (
        <>
          <Cpu size={48} color={STONE[400]} style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 16, fontWeight: 600, color: STONE[700], marginBottom: 8 }}>
            Ready to {(hasDocuments || hasFramework) ? 'Extract' : 'Generate'} Enforcements
          </div>
          <div style={{ fontSize: 13, color: STONE[500], marginBottom: 24, maxWidth: 560, margin: '0 auto 24px' }}>
            {hasFramework && <>Framework controls split into <strong>candidate rules</strong> (automatable) and <strong>attestation tasks</strong> (manual controls, raised in the Compliance Center on activate). </>}
            {hasDocuments && <>The RAG engine will analyze documents and extract enforceable rules using pattern matching across 6 policy domains. </>}
            {hasPatterns && <>Selected patterns will generate enforcement candidates with pre-configured rules and parameters. </>}
            {[hasFramework, hasDocuments, hasPatterns].filter(Boolean).length > 1 && <>Candidates from every selected source merge into one review list.</>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            {hasFramework && (
              <div style={{ padding: '8px 16px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12, color: '#166534' }}>
                <Shield size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{frameworkName || 'Framework controls'}
              </div>
            )}
            {hasDocuments && (
              <div style={{ padding: '8px 16px', borderRadius: 8, background: STONE[50], border: `1px solid ${STONE[200]}`, fontSize: 12 }}>
                <FileText size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{docCount} document(s)
              </div>
            )}
            {hasPatterns && (
              <div style={{ padding: '8px 16px', borderRadius: 8, background: BRAND.light, border: `1px solid ${BRAND.primary}30`, fontSize: 12, color: BRAND.dark }}>
                <Layers size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{patCount} pattern(s)
              </div>
            )}
          </div>
          <button onClick={onExtract} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px',
            fontSize: 14, fontWeight: 600, borderRadius: 10, border: 'none',
            background: BRAND.primary, color: '#fff', cursor: 'pointer',
          }}>
            <Cpu size={16} /> {(hasDocuments || hasFramework) ? 'Start Extraction' : 'Generate Candidates'}
          </button>
        </>
      )}

      {processing && (
        <>
          <RefreshCw size={36} color={BRAND.primary} style={{ animation: 'spin 1.5s linear infinite', marginBottom: 16 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: STONE[700], marginBottom: 6 }}>
            {hasDocuments ? 'Extracting & Generating Enforcements...' : 'Generating Enforcements from Patterns...'}
          </div>
          <div style={{ fontSize: 13, color: STONE[500] }}>
            {hasDocuments ? 'Classifying sections, matching patterns, scoring confidence, generating rules...'
              : 'Instantiating pattern templates, configuring parameters, scoring confidence...'}
          </div>
        </>
      )}

      {candidates.length > 0 && !processing && (
        <>
          <Check size={36} color="#16a34a" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 16, fontWeight: 600, color: STONE[700], marginBottom: 8 }}>
            {hasDocuments ? 'Extraction' : 'Generation'} Complete
          </div>
          <div style={{ fontSize: 13, color: STONE[500], marginBottom: 12 }}>
            Found <strong style={{ color: STONE[900] }}>{candidates.length}</strong> enforcement candidates from {sourceDesc}.
            {' '}<strong style={{ color: '#16a34a' }}>{candidates.filter(c => c.confidenceScore >= 75).length}</strong> are high-confidence (≥75%).
          </div>
          {/* Source breakdown */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {candidates.some(c => c._source === 'framework') && (
              <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' }}>
                <Shield size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                {candidates.filter(c => c._source === 'framework').length} framework rules
              </span>
            )}
            {candidates.some(c => c._source === 'framework-attestation') && (
              <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
                <FileCheck size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                {candidates.filter(c => c._source === 'framework-attestation').length} attestation task(s)
              </span>
            )}
            {candidates.some(c => c._source === 'document') && (
              <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: STONE[50], border: `1px solid ${STONE[200]}`, color: STONE[600] }}>
                <FileText size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                {candidates.filter(c => c._source === 'document').length} from documents
              </span>
            )}
            {candidates.some(c => c._source === 'pattern') && (
              <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: BRAND.light, border: `1px solid ${BRAND.primary}30`, color: BRAND.dark }}>
                <Layers size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                {candidates.filter(c => c._source === 'pattern').length} from patterns
              </span>
            )}
            {candidates.some(c => c._source === 'both') && (
              <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' }}>
                <Check size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                {candidates.filter(c => c._source === 'both').length} merged (both sources)
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Binding scope choice (this skill vs whole agent) ─────── */
function ScopeChoice({ value, onChange }) {
  const OPTIONS = [
    { key: 'agent', title: 'Whole agent', desc: 'Gates every skill this agent runs. The runtime PEP enforces these controls before any skill executes — no skill can sneak past them.' },
    { key: 'skill', title: 'This skill only', desc: 'Applies only to this policy skill. Other skills on the agent are not gated by these controls.' },
  ];
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: STONE[700], marginBottom: 8 }}>
        Where should these controls apply?
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {OPTIONS.map(o => {
          const active = value === o.key;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onChange(o.key)}
              style={{
                flex: '1 1 240px', textAlign: 'left', cursor: 'pointer',
                padding: '12px 14px', borderRadius: 10,
                border: `1.5px solid ${active ? BRAND.primary : STONE[200]}`,
                background: active ? BRAND.light : '#fff',
                boxShadow: active ? `0 0 0 3px ${BRAND.glow}` : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{
                  width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                  border: `4px solid ${active ? BRAND.primary : STONE[400]}`,
                  background: '#fff',
                }} />
                <span style={{ fontSize: 13.5, fontWeight: 650, color: STONE[900] }}>{o.title}</span>
                {o.key === 'agent' && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                    color: BRAND.dark, background: BRAND.light, border: `1px solid ${BRAND.primary}`,
                    borderRadius: 6, padding: '1px 6px',
                  }}>Recommended</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: STONE[600], lineHeight: 1.45 }}>{o.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Step 3: Review ───────────────────────────────────────── */
function StepReview({ candidates, selectedIds, onToggle, onSelectAll, onSelectNone, onSelectHighConf }) {
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? candidates
    : filter === 'high' ? candidates.filter(c => c.confidenceScore >= 75)
    : filter === 'selected' ? candidates.filter(c => selectedIds.has(c.id))
    : candidates;

  return (
    <div>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        padding: '12px 18px', background: STONE[50], borderRadius: 10,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: STONE[700] }}>
          {selectedIds.size} of {candidates.length} selected
        </span>
        <div style={{ flex: 1, minWidth: 40 }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={onSelectHighConf} style={toolbarBtn}>High Confidence</button>
          <button onClick={onSelectAll} style={toolbarBtn}>Select All</button>
          <button onClick={onSelectNone} style={toolbarBtn}>Clear</button>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={{
            ...selectStyle, width: 'auto', padding: '6px 30px 6px 10px', fontSize: 12,
          }}>
            <option value="all">All ({candidates.length})</option>
            <option value="high">High Confidence ({candidates.filter(c => c.confidenceScore >= 75).length})</option>
            <option value="selected">Selected ({selectedIds.size})</option>
          </select>
        </div>
      </div>

      {/* Candidate List — grouped by source so framework rules, manual→attestation
          tasks, document- and pattern-derived candidates are visually separated. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxHeight: 520, overflowY: 'auto', paddingRight: 8 }}>
        {(() => {
          const GROUPS = [
            { key: 'framework', label: 'Framework controls → candidate rules', accent: '#166534', bg: '#f0fdf4', border: '#bbf7d0' },
            { key: 'framework-attestation', label: 'Manual controls → attestation tasks', accent: '#92400e', bg: '#fffbeb', border: '#fde68a',
              note: 'Not enforced as deterministic rules — activating the policy creates attestation tasks in the Compliance Center for a human to evidence.' },
            { key: 'document', label: 'From policy documents', accent: STONE[600], bg: STONE[50], border: STONE[200] },
            { key: 'pattern', label: 'From pattern templates', accent: BRAND.dark, bg: BRAND.light, border: `${BRAND.primary}30` },
            { key: 'both', label: 'Merged (multiple sources)', accent: '#166534', bg: '#f0fdf4', border: '#bbf7d0' },
          ];
          const known = new Set(GROUPS.map(g => g.key));
          const groups = GROUPS
            .map(g => ({ ...g, items: filtered.filter(c => (c._source || '') === g.key) }))
            .filter(g => g.items.length > 0);
          const other = filtered.filter(c => !known.has(c._source || ''));
          if (other.length) groups.push({ key: 'other', label: 'Other candidates', accent: STONE[600], bg: STONE[50], border: STONE[200], items: other });

          if (filtered.length === 0) {
            return (
              <div style={{ textAlign: 'center', padding: 32, color: STONE[400], fontSize: 13 }}>
                No candidates match the current filter
              </div>
            );
          }
          return groups.map(g => (
            <div key={g.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: g.accent, background: g.bg, border: `1px solid ${g.border}`, padding: '3px 10px', borderRadius: 6,
                }}>
                  {g.label}
                </span>
                <span style={{ fontSize: 12, color: STONE[400], fontWeight: 600 }}>
                  {g.items.filter(c => selectedIds.has(c.id)).length}/{g.items.length} selected
                </span>
              </div>
              {g.note && (
                <div style={{ fontSize: 11.5, color: g.accent, marginBottom: 8, lineHeight: 1.4 }}>{g.note}</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {g.items.map(c => (
                  <CandidateRow
                    key={c.id}
                    candidate={c}
                    selected={selectedIds.has(c.id)}
                    onToggle={() => onToggle(c.id)}
                  />
                ))}
              </div>
            </div>
          ));
        })()}
      </div>
    </div>
  );
}

const toolbarBtn = {
  padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 6,
  border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[600],
  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
};

function CandidateRow({ candidate: c, selected, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  const confColor = c.confidenceScore >= 75 ? '#16a34a' : c.confidenceScore >= 50 ? '#ca8a04' : BRAND.primary;

  return (
    <div style={{
      border: `1px solid ${selected ? BRAND.primary + '40' : STONE[200]}`,
      borderRadius: 10,
      background: selected ? BRAND.light + '40' : '#fff',
    }}>
      {/* Main row — use CSS grid for stable column widths */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '24px 1fr auto auto',
          gap: 14,
          alignItems: 'center',
          padding: '14px 16px',
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        {/* Checkbox */}
        <input
          type="checkbox" checked={selected} onChange={onToggle}
          style={{ width: 18, height: 18, accentColor: BRAND.primary, cursor: 'pointer', justifySelf: 'center' }}
          onClick={e => e.stopPropagation()}
        />

        {/* Name + metadata */}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: STONE[900], marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {c.proposedRuleName}
          </div>
          <div style={{ fontSize: 11, color: STONE[500], display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace", padding: '2px 8px', borderRadius: 4,
              background: STONE[100], color: STONE[600], fontSize: 10, fontWeight: 500,
              whiteSpace: 'nowrap',
            }}>{c.proposedRuleType}</span>
            <span style={{ whiteSpace: 'nowrap' }}>Severity: <strong>{c.proposedSeverity}</strong></span>
            {c.sectionHeader && <span style={{ color: STONE[400] }}>{c.sectionHeader}</span>}
            {/* Source attribution badge */}
            {c._source === 'framework' && (
              <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>FRAMEWORK</span>
            )}
            {c._source === 'framework-attestation' && (
              <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>ATTESTATION TASK</span>
            )}
            {c._source === 'pattern' && (
              <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: BRAND.light, color: BRAND.dark, border: `1px solid ${BRAND.primary}30` }}>PATTERN</span>
            )}
            {c._source === 'document' && (
              <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: STONE[50], color: STONE[600], border: `1px solid ${STONE[200]}` }}>DOCUMENT</span>
            )}
            {c._source === 'both' && (
              <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>MERGED</span>
            )}
            {c.patternName && <span style={{ color: STONE[400], fontStyle: 'italic' }}>via {c.patternName}</span>}
          </div>
        </div>

        {/* Confidence score — fixed-width so it never clips */}
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingLeft: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: confColor }}>{c.confidenceScore}%</span>
        </div>

        {/* Expand chevron */}
        <button onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} style={{
          border: 'none', background: STONE[50], borderRadius: 6, width: 28, height: 28,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: STONE[500], padding: 0,
          transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s',
        }}>
          <ChevronDown size={14} />
        </button>
      </div>

      {/* Expanded detail panel */}
      {expanded && (
        <div style={{ padding: '0 16px 14px 52px', fontSize: 12, color: STONE[600] }}>
          {c.sourcePassage && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, background: STONE[50],
              borderLeft: `3px solid ${BRAND.primary}40`, marginBottom: 10,
              fontStyle: 'italic', lineHeight: 1.5,
            }}>
              "{c.sourcePassage.substring(0, 300)}{c.sourcePassage.length > 300 ? '...' : ''}"
            </div>
          )}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 11, color: STONE[500] }}>
            <span>Language Clarity: {c.languageClarity}%</span>
            <span>Pattern Match: {c.patternMatchQuality}%</span>
            <span>Param Extraction: {c.paramExtractability}%</span>
            <span>Context: {c.contextConsistency}%</span>
            {c.pageNumber && <span>Page: {c.pageNumber}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Phase 8: Classification-aware agent picker ───────────────────────
   When scope=AGENT, operators choose targets either individually or by
   classification. Classification multi-select acts as a filter PLUS a
   "Select all matching" affordance: pick "EXTERNAL_API" + click "Select all"
   and every external-API agent gets added to the binding.

   Source field: agent.agentClassification (V129 — INTERNAL | EXTERNAL_API |
   EXTERNAL_SDK | HYBRID). agents missing the field are treated as legacy
   INTERNAL so the picker never hides them. ─────────────────────────────── */
const CLASSIFICATION_OPTIONS = [
  { id: 'INTERNAL',     label: 'Internal',      desc: 'Agentlets-built agent' },
  { id: 'EXTERNAL_API', label: 'External API',  desc: 'Vendor agent invoked over HTTP' },
  { id: 'EXTERNAL_SDK', label: 'External SDK',  desc: 'Vendor agent linked via SDK' },
  { id: 'HYBRID',       label: 'Hybrid',        desc: 'Mix of internal logic + vendor calls' },
];

function AgentPickerWithClassification({ agents, selectedIds, onChange, scopeAgentId }) {
  const [classFilter, setClassFilter] = useState([]); // [] = no filter
  const [search, setSearch] = useState('');

  const realAgents = (agents || []).filter(a => !a.isSystemAgent);
  // Name of the agent this wizard was opened from — pre-selected below. Shown so
  // the operator understands why the list contains every agent in their tenant:
  // binding is multi-agent by design, and their agent is already ticked.
  const originAgent = scopeAgentId
    ? realAgents.find(a => String(a.id) === String(scopeAgentId))
    : null;

  // Counts per classification — surface "INTERNAL (12)" alongside the
  // filter chip so operators see scope size before clicking.
  const counts = React.useMemo(() => {
    const c = {};
    for (const a of realAgents) {
      const k = a.agentClassification || 'INTERNAL';
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [realAgents]);

  const matchesClassification = (a) => {
    if (classFilter.length === 0) return true;
    return classFilter.includes(a.agentClassification || 'INTERNAL');
  };
  const matchesSearch = (a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (a.name || '').toLowerCase().includes(q)
        || (a.id || '').toLowerCase().includes(q);
  };
  const visible = realAgents.filter(a => matchesClassification(a) && matchesSearch(a));

  const toggleClass = (k) => {
    setClassFilter(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  };
  const selectAllVisible = () => {
    const ids = new Set(selectedIds);
    visible.forEach(a => ids.add(a.id));
    onChange(Array.from(ids));
  };
  const clearVisibleSelection = () => {
    const visibleSet = new Set(visible.map(a => a.id));
    onChange(selectedIds.filter(id => !visibleSet.has(id)));
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: STONE[700] }}>
          Select Agents
        </label>
        <span style={{ fontSize: 11, color: STONE[500] }}>
          {selectedIds.length} of {realAgents.length} selected · {visible.length} visible after filters
        </span>
      </div>
      <div style={{ fontSize: 11, color: STONE[500], marginBottom: 8, lineHeight: 1.5 }}>
        {originAgent
          ? `${originAgent.name} is already selected. This control can also cover other agents in your tenant — add any others you want it to enforce on, or leave just this one.`
          : 'Every agent in your tenant is listed. Select each one this control should enforce on.'}
      </div>

      {/* Classification filter chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {CLASSIFICATION_OPTIONS.map(opt => {
          const active = classFilter.includes(opt.id);
          const n = counts[opt.id] || 0;
          return (
            <button
              key={opt.id}
              onClick={() => toggleClass(opt.id)}
              title={opt.desc}
              style={{
                padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 999,
                border: `1px solid ${active ? BRAND.primary : STONE[200]}`,
                background: active ? BRAND.light : '#fff',
                color: active ? BRAND.primary : (n === 0 ? STONE[400] : STONE[600]),
                cursor: n === 0 ? 'not-allowed' : 'pointer',
                opacity: n === 0 ? 0.5 : 1,
              }}
              disabled={n === 0}
            >
              {opt.label} <span style={{ fontWeight: 500, opacity: 0.7 }}>({n})</span>
            </button>
          );
        })}
        {classFilter.length > 0 && (
          <button onClick={() => setClassFilter([])} style={{
            padding: '4px 10px', fontSize: 11, fontWeight: 500, borderRadius: 999,
            border: 'none', background: 'transparent', color: STONE[500], cursor: 'pointer',
          }}>Clear filter</button>
        )}
      </div>

      {/* Search + bulk actions */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search agents…"
          style={{
            flex: 1, padding: '6px 10px', fontSize: 12, borderRadius: 6,
            border: `1px solid ${STONE[200]}`, outline: 'none', fontFamily: 'inherit',
          }}
        />
        <button onClick={selectAllVisible} disabled={visible.length === 0}
                style={{
                  padding: '6px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                  border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[600],
                  cursor: visible.length === 0 ? 'not-allowed' : 'pointer',
                }}>
          Select all visible ({visible.length})
        </button>
        <button onClick={clearVisibleSelection} disabled={visible.length === 0}
                style={{
                  padding: '6px 12px', fontSize: 11, fontWeight: 500, borderRadius: 6,
                  border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[600],
                  cursor: visible.length === 0 ? 'not-allowed' : 'pointer',
                }}>
          Clear visible
        </button>
      </div>

      {/* Agent list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
        {visible.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', color: STONE[400], fontSize: 12 }}>
            No agents match the current filter.
          </div>
        )}
        {visible.map(agent => {
          const isSelected = selectedIds.includes(agent.id);
          const klass = agent.agentClassification || 'INTERNAL';
          return (
            <label key={agent.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
              borderRadius: 8, border: `1px solid ${isSelected ? BRAND.primary + '40' : STONE[200]}`,
              background: isSelected ? BRAND.light + '40' : '#fff', cursor: 'pointer',
            }}>
              <input type="checkbox" checked={isSelected}
                onChange={() => {
                  onChange(isSelected
                    ? selectedIds.filter(id => id !== agent.id)
                    : [...selectedIds, agent.id]);
                }}
                style={{ accentColor: BRAND.primary }}
              />
              <span style={{ fontSize: 13, color: STONE[700], flex: 1, minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {agent.name}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                background: STONE[100], color: STONE[600], whiteSpace: 'nowrap',
              }}>
                {klass.replace(/_/g, ' ')}
              </span>
              <span style={{ fontSize: 11, color: STONE[400] }}>{agent.status}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Step 4: Binding ──────────────────────────────────────── */
//
// Spec: docs/UNIFIED_POLICY_PIPELINE_SPEC.md (Stage 4 — CONFIGURE).
//
// Three sub-panels in priority order so the operator sees inherited
// policy first (don't duplicate), then picks scope + adaptive PEPs, then
// reviews framework coverage if framework context is present.
//
//   ┌─────────────────────────────────────────────┐
//   │ Inheritance preview (read-only)             │  ← what's already in effect
//   ├─────────────────────────────────────────────┤
//   │ Scope picker  +  Agent picker               │  ← who this targets
//   ├─────────────────────────────────────────────┤
//   │ Adaptive PEP picker                         │  ← only PEPs that can fire
//   ├─────────────────────────────────────────────┤
//   │ Framework coverage matrix (conditional)     │  ← only with frameworkBindingId
//   └─────────────────────────────────────────────┘
//
// Capability inference: PEP applicability depends on what the *target*
// can actually emit. We derive per-agent capabilities from agent skills
// (workflow_skill present? connectors present? mcp_tools? etc) and pass
// those flags to the backend. For ORG/TENANT scope (no specific agent),
// we send `null` for each capability and the backend falls back to the
// permissive superset — admin can narrow further if they wish.
function StepBinding({
  bindingConfig, setBindingConfig, agents, selectedCount,
  frameworkBindingId, frameworkId, frameworkName,
  candidates, selectedIds,
  materialized, scopeAgentId, onStatsChange,
}) {
  const [pepCatalog, setPepCatalog] = useState([]);     // [{id,label,description,applicable}]
  const [inherited, setInherited] = useState(null);     // {inheritedBindings, count}
  const [loadingPeps, setLoadingPeps] = useState(false);
  const [loadingInherited, setLoadingInherited] = useState(false);

  // Report the numbers the operator sees here UP to the shell, so the Activate
  // summary shows the exact same counts (single source of truth — the Activate
  // tiles never compute their own). applicablePepCount = enforcement points that
  // actually apply to this target; inheritedCount = controls already cascading in.
  const applicablePepCount = pepCatalog.filter(p => p.applicable).length;
  const inheritedCount = inherited?.count ?? 0;
  useEffect(() => {
    if (onStatsChange) onStatsChange({ applicablePepCount, inheritedCount });
  }, [applicablePepCount, inheritedCount, onStatsChange]);

  const update = (key, value) => setBindingConfig(prev => ({ ...prev, [key]: value }));

  // ── Resolve effective context from current bindingConfig ─────────
  // For PEP applicability we need to know the *target's* capabilities.
  // - AGENT scope with one agent picked → derive capabilities from that agent
  // - AGENT scope with multiple agents → conservative superset (any agent's caps)
  // - ORG/TENANT scope → return null caps so backend gives the superset
  const selectedAgents = (bindingConfig.agentIds || [])
    .map(id => (agents || []).find(a => a.id === id))
    .filter(Boolean);

  const capability = (() => {
    if (bindingConfig.scope !== 'AGENT' || selectedAgents.length === 0) {
      return { hasWorkflowSkill: null, hasMcpTools: null, emitsLlmCalls: null,
               hasMultiStepWorkflow: null, hasConnectorOut: null, agentClass: null };
    }
    const anySkill = (pred) => selectedAgents.some(a => (a.skills || []).some(pred));
    const anyConnector = () => selectedAgents.some(a => (a.connectors || []).length > 0);
    const hasWorkflowSkill = anySkill(s => s.skillType === 'workflow_skill');
    // An MCP-gateway ExteLet routes its tools/call through the gateway but carries
    // NO native mcp_skill/mcp_tool rows — so detect external agents explicitly,
    // otherwise MCP Tool Call would wrongly grey out for the very agents whose
    // tool dispatch the gateway enforces.
    const anyExternal = selectedAgents.some(a => a.isExternal || a.external
      || a.gel || a.agentClassification === 'EXTERNAL' || a.type === 'external');
    const hasMcpTools = anyExternal
      || anySkill(s => s.skillType === 'mcp_skill' || s.skillType === 'mcp_tool');
    // Any agent that backs an LLM-driven role emits inference calls.
    // Conservative default: true — only false when explicitly tagged otherwise.
    const emitsLlmCalls = true;
    // Multi-step is implied by workflow skill presence; we don't have
    // step-count metadata client-side so this is best effort.
    const hasMultiStepWorkflow = hasWorkflowSkill;
    const hasConnectorOut = anyConnector();
    // P1 — single-agent scope carries the integration class so the backend can
    // return the substrate-accurate PEP set (MCP gateway vs SDK decorator vs
    // native). Mixed/multi-agent → null (backend uses the capability superset).
    const agentClass = selectedAgents.length === 1
      ? (selectedAgents[0].agentClassification || 'INTERNAL')
      : null;
    return { hasWorkflowSkill, hasMcpTools, emitsLlmCalls, hasMultiStepWorkflow, hasConnectorOut, agentClass };
  })();

  // ── Load adaptive PEPs whenever capability or scope changes ──────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPeps(true);
      try {
        const res = await api.getApplicablePepSet(capability);
        if (cancelled) return;
        if (res.ok && Array.isArray(res.data?.peps)) setPepCatalog(res.data.peps);
        else setPepCatalog([]);
      } catch (e) {
        if (!cancelled) setPepCatalog([]);
      } finally {
        if (!cancelled) setLoadingPeps(false);
      }
    })();
    return () => { cancelled = true; };
    // capability is a fresh object every render; we stringify it so the
    // dep array is stable across renders and we only re-fetch when an
    // actual flag changes.
  }, [JSON.stringify(capability)]);

  // ── Load inherited bindings when scope context resolves ──────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingInherited(true);
      try {
        // We don't know orgId/tenantId in this client-side view today
        // (the wizard stays inside the current admin's tenant). Pass the
        // resolved agent id when AGENT scope is chosen — backend uses it
        // to skip bindings already pinned to that agent.
        const params = {};
        if (bindingConfig.scope === 'AGENT' && selectedAgents.length === 1) {
          params.agentId = selectedAgents[0].id;
        }
        const res = await api.getInheritedPolicyBindings(params);
        if (cancelled) return;
        if (res.ok) setInherited(res.data);
        else setInherited(null);
      } catch (e) {
        if (!cancelled) setInherited(null);
      } finally {
        if (!cancelled) setLoadingInherited(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bindingConfig.scope, JSON.stringify(bindingConfig.agentIds)]);

  // ── Coverage matrix (only with framework context) ────────────────
  // Splits the selected candidates into rules vs attestations and shows
  // counts so the operator can see the framework coverage they're about
  // to commit to.
  const coverage = (() => {
    if (!frameworkBindingId) return null;
    const selectedCandidates = (candidates || []).filter(c => selectedIds && selectedIds.has(c.id));
    const fromFramework = selectedCandidates.filter(c => c._source === 'framework' || c._source === 'framework-attestation');
    const ruleCount = fromFramework.filter(c => c._source === 'framework').length;
    const attestCount = fromFramework.filter(c => c._source === 'framework-attestation').length;
    return { ruleCount, attestCount, totalCovered: ruleCount + attestCount };
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{
        padding: '12px 16px', borderRadius: 10, background: BRAND.light,
        fontSize: 13, color: BRAND.dark, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <CheckSquare size={16} /> {selectedCount} enforcements selected for binding
      </div>

      {/* Controls this policy skill already enforces — same panel as the Source
          step, shown here so binding is never a blind restart. */}
      <EnforcingPanel materialized={materialized} />

      {/* ── Augment Existing Scope (Phase 8) ──────────────────────────
            When bindings already cascade into this scope, this is an
            augmentation pass — the operator is layering ADDITIONAL
            controls on top of what's already in effect. The header
            language and accent colour change to make that clear. */}
      {(() => {
        const isAugmenting = inheritedCount > 0;
        const headerBg = isAugmenting ? '#eff6ff' : STONE[50];
        const headerBorder = isAugmenting ? '#bfdbfe' : STONE[200];
        const headerText = isAugmenting ? '#1e40af' : STONE[700];
        return (
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            border: `1px solid ${headerBorder}`, background: headerBg,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Layers size={14} color={isAugmenting ? '#1e40af' : STONE[600]} />
              <div style={{ fontSize: 13, fontWeight: 700, color: headerText }}>
                {isAugmenting
                  ? `Augmenting ${inheritedCount} control${inheritedCount === 1 ? '' : 's'} already in effect`
                  : 'No bindings cascade into this scope'}
              </div>
              <div style={{ marginLeft: 'auto', fontSize: 11, color: STONE[500] }}>
                {loadingInherited ? 'Loading…' : `${selectedCount} new · ${inheritedCount} inherited`}
              </div>
            </div>
            {!loadingInherited && !isAugmenting && (
              <div style={{ fontSize: 12, color: STONE[500] }}>
                You're starting from a clean slate at this scope. The new
                bindings you create here will be the only ones in effect.
              </div>
            )}
            {isAugmenting && (
              <div style={{ fontSize: 12, color: STONE[700], marginBottom: 8, lineHeight: 1.5 }}>
                The controls below are already in effect from broader scopes and
                will keep enforcing. Your new selections layer on top — both
                apply together.
              </div>
            )}
            {(inherited?.inheritedBindings || []).slice(0, 5).map((b, i) => {
              // Humane source label — never surface raw scope enums or the
              // PLATFORM_DEFAULT tag to the operator. Platform-seeded baselines
              // carry sourceTag=PLATFORM_DEFAULT even when scoped to the tenant.
              const src = b.sourceTag === 'PLATFORM_DEFAULT' || b.inheritedFrom === 'PLATFORM'
                ? 'Platform baseline'
                : b.inheritedFrom === 'TENANT' ? 'Organization-wide'
                : b.inheritedFrom === 'ORG' ? 'Organization'
                : b.inheritedFrom === 'AGENT' ? 'Agent-specific'
                : 'Inherited';
              return (
                <div key={i} title={b.ruleDescription || undefined} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '4px 0', fontSize: 12, color: STONE[600],
                }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                    background: '#e0e7ff', color: '#3730a3',
                  }}>
                    {src}
                  </span>
                  <span style={{ color: STONE[700] }}>{b.ruleName || 'Governance control'}</span>
                </div>
              );
            })}
            {(inherited?.inheritedBindings || []).length > 5 && (
              <div style={{ fontSize: 11, color: STONE[500], marginTop: 6 }}>
                …and {inherited.inheritedBindings.length - 5} more
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Scope picker ──────────────────────────────────────────── */}
      <div>
        <label style={{ fontSize: 13, fontWeight: 600, color: STONE[700], display: 'block', marginBottom: 6 }}>
          Enforcement Scope
        </label>
        <select value={bindingConfig.scope} onChange={e => update('scope', e.target.value)} style={selectStyle}>
          <option value="TENANT">Tenant-wide (all agents in this tenant)</option>
          <option value="ORG">Organization-wide (all agents in this org)</option>
          <option value="AGENT">Specific agents only</option>
        </select>
      </div>

      {bindingConfig.scope === 'AGENT' && (
        <AgentPickerWithClassification
          agents={agents}
          selectedIds={bindingConfig.agentIds || []}
          onChange={(ids) => update('agentIds', ids)}
          scopeAgentId={scopeAgentId}
        />
      )}

      {/* ── Adaptive PEP picker ───────────────────────────────────── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: STONE[700] }}>
            Enforcement Points
          </label>
          <span style={{ fontSize: 11, color: STONE[500] }}>
            {loadingPeps ? 'Loading…' :
              `${pepCatalog.filter(p => p.applicable).length} applicable to this target`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {pepCatalog.length === 0 && !loadingPeps && (
            <span style={{ fontSize: 12, color: STONE[500] }}>
              No PEP info — proceeding will bind to PEP-0 (Agent Entry) as a safe default.
            </span>
          )}
          {pepCatalog.map(pep => {
            const isActive = (bindingConfig.enforcementPoints || []).includes(pep.id);
            const dim = !pep.applicable;
            return (
              <button
                key={pep.id}
                title={dim
                  ? (pep.reason || (pep.description + ' — not applicable to this target'))
                  : (pep.reason || pep.description)}
                disabled={dim}
                onClick={() => {
                  const current = bindingConfig.enforcementPoints || [];
                  update('enforcementPoints',
                    isActive ? current.filter(x => x !== pep.id) : [...current, pep.id]);
                }}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 8,
                  border: `1px solid ${isActive ? BRAND.primary : STONE[200]}`,
                  background: isActive ? BRAND.light : '#fff',
                  color: isActive ? BRAND.primary : (dim ? STONE[400] : STONE[600]),
                  cursor: dim ? 'not-allowed' : 'pointer',
                  opacity: dim ? 0.5 : 1,
                }}
              >
                {pep.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Framework coverage matrix (only with framework context) ─ */}
      {frameworkBindingId && coverage && (
        <div style={{
          padding: '14px 16px', borderRadius: 10,
          border: '1px solid #bfdbfe', background: '#eff6ff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Shield size={14} color="#1e40af" />
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>
              Framework Coverage — {frameworkName || 'Framework'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, fontSize: 12, color: STONE[700] }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af' }}>
                {coverage.ruleCount}
              </div>
              <div style={{ fontSize: 11, color: STONE[500] }}>Automatable rules</div>
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#92400e' }}>
                {coverage.attestCount}
              </div>
              <div style={{ fontSize: 11, color: STONE[500] }}>Attestation tasks</div>
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: STONE[700] }}>
                {coverage.totalCovered}
              </div>
              <div style={{ fontSize: 11, color: STONE[500] }}>Total controls covered</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Step 5: Activation ───────────────────────────────────── */
function StepActivation({ selectedCount, inheritedCount, applicablePepCount, dryRunResults, onDryRun, processing }) {
  // All three tiles are driven by the SAME numbers the operator saw on the
  // Configure Binding step (threaded up via bindStats) — no hardcoded counts,
  // so this summary can never contradict the previous page. A null means the
  // operator jumped straight here without visiting Bind; show a neutral dash.
  const dash = (n) => (n === null || n === undefined ? '—' : n);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{
        display: 'flex', gap: 20, padding: 24, background: STONE[50], borderRadius: 12,
      }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: STONE[900] }}>{selectedCount}</div>
          <div style={{ fontSize: 12, color: STONE[500] }}>New Controls</div>
        </div>
        <div style={{ width: 1, background: STONE[200] }} />
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: BRAND.primary }}>{dash(inheritedCount)}</div>
          <div style={{ fontSize: 12, color: STONE[500] }}>Already in Effect</div>
        </div>
        <div style={{ width: 1, background: STONE[200] }} />
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#16a34a' }}>{dash(applicablePepCount)}</div>
          <div style={{ fontSize: 12, color: STONE[500] }}>Enforcement Points</div>
        </div>
      </div>

      {/* Dry Run */}
      <div style={{
        padding: '16px 20px', borderRadius: 12, border: `1px solid ${STONE[200]}`, background: '#fff',
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: STONE[700], marginBottom: 8 }}>
          Test Before Activating
        </div>
        <div style={{ fontSize: 13, color: STONE[500], marginBottom: 14 }}>
          Switch to testing mode to evaluate rules against live traffic without blocking. Review results before full activation.
        </div>
        <button onClick={onDryRun} disabled={processing} style={{
          padding: '8px 16px', fontSize: 13, fontWeight: 500, borderRadius: 8,
          border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[600],
          cursor: processing ? 'not-allowed' : 'pointer',
        }}>
          {processing ? 'Starting...' : 'Enable Testing Mode'}
        </button>
        {dryRunResults && (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#f0fdf4',
            border: '1px solid #bbf7d0', fontSize: 13, color: '#166534',
          }}>
            {dryRunResults.message}
          </div>
        )}
      </div>

      {/* Activation Info */}
      <div style={{
        padding: '16px 20px', borderRadius: 12, border: `1px solid ${BRAND.primary}20`,
        background: BRAND.light,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: BRAND.dark, marginBottom: 8 }}>
          Full Activation
        </div>
        <div style={{ fontSize: 13, color: STONE[600], lineHeight: 1.6 }}>
          Clicking "Activate Policy Skill" will enable all selected rules in production.
          Rules will be enforced deterministically at configured PEP checkpoints before any LLM execution.
          BLOCK-severity rules will reject non-compliant requests immediately.
        </div>
      </div>
    </div>
  );
}
